(function () {
  'use strict';

  const ERR = function () { console.error.apply(console, ['[SIM]'].concat(Array.prototype.slice.call(arguments))); };

  function debugPanel(msg) {
    const el = document.getElementById('sim-debug');
    if (el) el.textContent += msg + '\n';
  }

  function cleanStr(str) {
    return (str || '')
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();
  }

  const CONFIG = {
    storefrontToken: 'dff0d69a99d7827402d33af0a3de3870',
    shopDomain: 'cavissima.com',
    apiVersion: '2024-10',
    currentYear: new Date().getFullYear(),
  };

  const KLAVIYO_PUBLIC_KEY = 'RnqMrF';
  // Encodé en base64 pour éviter qu'il soit trouvable en clair par un simple Ctrl+F / vue source
  // (ce n'est PAS une protection réelle : quiconque inspecte les requêtes réseau le voit quand même).
  const MONITORING_EMAIL = atob('ZXdhbi5ncnVpbGxvdEBjYXZpc3NpbWEuY29t');

  // ---- GA4 : SUIVI DU TUNNEL (démarrage, étapes, résultats, PDF, panier) ----
  // Le thème charge déjà GA4 (gtag.js) sur tout le site — on réutilise window.gtag s'il existe,
  // sinon on pousse directement dans window.dataLayer (fonctionne aussi si GA4 est géré via
  // Google Tag Manager plutôt que gtag.js directement). Ne casse jamais le simulateur si GA4 est
  // absent (ex: bloqueur de pub) : silencieux dans ce cas, jamais d'erreur remontée à l'utilisateur.
  function trackEvent(eventName, params) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params || {});
      } else if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: eventName }, params || {}));
      }
    } catch (e) {
      ERR('trackEvent:', e);
    }
  }

  const state = {
    currentStep: 0,
    answers: {
      projet: [], situation: null, capacite: null,
      budget: null, budgetMax: null, quantite: [],
      couleurs: [], regions: [],
      formats: [],
    },
    products: [],
    idealCellar: [],
  };

  const BUDGET_MAP = {
    '20-50':    { min: 20,  max: 50 },
    '50-80':    { min: 50,  max: 80 },
    '80-120':   { min: 80,  max: 120 },
    'gt120':    { min: 120, max: 9999 },
  };

  const CAPACITE_MAP = {
    'lt50': 40,
    '50-150': 100,
    '150-500': 250,
    'gt500': 500,
  };

  const HORIZON_PROJET_MAP = {
    'plaisir':    { court: 60, moyen: 30, long: 10 },
    'occasions':  { court: 30, moyen: 50, long: 20 },
    'patrimoine': { court: 10, moyen: 40, long: 50 },
  };

  // Objectifs multi-sélectionnables : moyenne simple des répartitions court/moyen/long de
  // chaque objectif coché (aucun signal de priorité entre eux dans l'UI, donc pondération
  // égale) — ex. "Plaisir quotidien" + "Patrimoine" donne une cave à mi-chemin entre les deux.
  function getHorizonPercentages(selectedProjets) {
    const keys = (selectedProjets && selectedProjets.length) ? selectedProjets : ['occasions'];
    const sums = { court: 0, moyen: 0, long: 0 };
    keys.forEach(function(p) {
      const h = HORIZON_PROJET_MAP[p] || HORIZON_PROJET_MAP.occasions;
      sums.court += h.court;
      sums.moyen += h.moyen;
      sums.long += h.long;
    });
    return {
      court: Math.round(sums.court / keys.length),
      moyen: Math.round(sums.moyen / keys.length),
      long: Math.round(sums.long / keys.length),
    };
  }

  const FORMAT_BASE_WEIGHTS = { '6': 45, '3': 30, '1': 10 };

  function getFormatsPercentages(selectedFormats) {
    if (!selectedFormats || !selectedFormats.length) {
      return { '6': 100 };
    }
    const totalWeight = selectedFormats.reduce(function(sum, f) { return sum + (FORMAT_BASE_WEIGHTS[f] || 0); }, 0);
    const res = {};
    selectedFormats.forEach(function(f) {
      res[f] = totalWeight > 0 ? Math.round((FORMAT_BASE_WEIGHTS[f] / totalWeight) * 100) : 0;
    });
    return res;
  }

  function getCouleursPercentages(selectedColors) {
    if (!selectedColors || selectedColors.length === 0) {
      return { rouge: 100, blanc: 0, rose: 0, champagne: 0 };
    }

    const hasRouge = selectedColors.includes('rouge');
    const hasBlanc = selectedColors.includes('blanc');
    const hasRose = selectedColors.includes('rose');
    const hasChampagne = selectedColors.includes('champagne');

    if (selectedColors.length === 1) {
      return {
        rouge: hasRouge ? 100 : 0,
        blanc: hasBlanc ? 100 : 0,
        rose: hasRose ? 100 : 0,
        champagne: hasChampagne ? 100 : 0,
      };
    }

    if (hasRouge && hasBlanc && !hasRose && !hasChampagne) return { rouge: 70, blanc: 30, rose: 0, champagne: 0 };
    if (hasRouge && hasBlanc && hasChampagne && !hasRose) return { rouge: 60, blanc: 25, rose: 0, champagne: 15 };
    if (hasRouge && hasBlanc && hasRose && hasChampagne) return { rouge: 50, blanc: 30, rose: 10, champagne: 10 };
    if (hasRouge && hasChampagne && !hasBlanc && !hasRose) return { rouge: 85, blanc: 0, rose: 0, champagne: 15 };
    if (hasBlanc && hasChampagne && !hasRouge && !hasRose) return { rouge: 0, blanc: 65, rose: 0, champagne: 35 };

    if (hasRouge && hasRose && hasChampagne && !hasBlanc) return { rouge: 70, blanc: 0, rose: 15, champagne: 15 };

    let weights = { rouge: 5, blanc: 3, champagne: 2, rose: 1 };
    let totalWeight = selectedColors.reduce(function(sum, c) { return sum + (weights[c] || 1); }, 0);

    let res = { rouge: 0, blanc: 0, rose: 0, champagne: 0 };
    selectedColors.forEach(function(c) {
      res[c] = Math.round(((weights[c] || 1) / totalWeight) * 100);
    });
    return res;
  }

  function getVariantPackSize(title) {
    const t = cleanStr(title);
    // Ancré en début de chaîne ("N x ...") ou sur "carton/caisse de N" — un includes() nu
    // matchait à tort un volume comme "300cl" (contient "3") ou "600cl" (contient "6").
    //
    // Lookahead (?=\d) plutôt que \b après le "x" : \b exige qu'un chiffre soit suivi d'un
    // caractère non-mot, hors un chiffre est lui-même un caractère de mot — donc "1 x 75cl"
    // (espace avant le chiffre) matchait, mais "1x75cl" (aucun espace, chiffre collé au x)
    // échouait silencieusement et retombait sur la valeur par défaut (1 bouteille) au lieu du
    // vrai conditionnement. Le lookahead ne regarde que "x suivi d'un chiffre", peu importe
    // les espaces de chaque côté.
    let m = t.match(/^(\d+)\s*x\s*(?=\d)/);
    if (m) return parseInt(m[1], 10);
    m = t.match(/(?:carton|caisse)\s+de\s+(\d+)/);
    if (m) return parseInt(m[1], 10);
    return 1;
  }

  // Détecte le format bouteille (demi/standard/magnum/jéroboam) depuis le volume en cl
  // présent dans le titre de variante — c'est ce volume, pas un tag produit, qui reflète
  // le format physique réellement reçu par le client.
  function getVariantBottleFormat(title) {
    const t = cleanStr(title);
    const m = t.match(/(\d+(?:[.,]\d+)?)\s*cl/);
    if (!m) return 'standard';
    const cl = parseFloat(m[1].replace(',', '.'));
    if (cl > 0 && cl <= 40) return 'demi';
    if (cl >= 140 && cl <= 160) return 'magnum';
    if (cl >= 280 && cl <= 320) return 'jeroboam';
    if (cl >= 580 && cl <= 620) return 'imperiale';
    return 'standard';
  }

  function getMatchingVariants(variantsEdges, allowedFormats) {
    const desiredBottleFormats = (state.answers.formats && state.answers.formats.length) ? state.answers.formats : ['standard'];
    const availableVariants = (variantsEdges || [])
      .map(function(e) { return e.node; })
      .filter(function(v) { return v.availableForSale; });

    const bySize = {};
    availableVariants.forEach(function(v) {
      if (!desiredBottleFormats.includes(getVariantBottleFormat(v.title))) return;
      const size = String(getVariantPackSize(v.title));
      if (allowedFormats.indexOf(size) === -1) return;
      const price = (v.price && v.price.amount) ? parseFloat(v.price.amount) : 0;
      const compareAtPrice = (v.compareAtPrice && v.compareAtPrice.amount) ? parseFloat(v.compareAtPrice.amount) : null;
      if (!bySize[size] || price < bySize[size].price) {
        bySize[size] = { variant: v, price: price, compareAtPrice: compareAtPrice };
      }
    });

    return bySize; // { '3': { variant, price }, '6': { variant, price } }
  }

  function getProductColorCategory(p) {
    const c = cleanStr(p.color);
    const r = cleanStr(p.region);
    const t = p.tags.map(function(tag) { return cleanStr(tag); }).join(' ');

    if (c.includes('champagne') || c.includes('effervescent') || c.includes('bulle') || r.includes('champagne') || t.includes('champagne')) {
      return 'champagne';
    }
    if (c.includes('rouge') || c.includes('red') || t.includes('rouge')) return 'rouge';
    if (c.includes('blanc') || c.includes('white') || t.includes('blanc')) return 'blanc';
    if (c.includes('rose') || t.includes('rose')) return 'rose';

    return 'autre';
  }

  const REGION_KEYWORDS = {
    bordeaux: ['bordeaux'],
    bourgogne: ['bourgogne'],
    rhone: ['rhone'],
    loire: ['loire'],
    languedoc: ['languedoc'],
    champagne: ['champagne'],
    beaujolais: ['beaujolais'],
    provence: ['provence'],
    autresfr: ['jura', 'sud-ouest', 'sud ouest', 'alsace', 'savoie'],
    italie: ['italie'],
    usa: ['usa'],
    australie: ['australie'],
    nouvellezelande: ['nouvelle zelande', 'nouvelle-zelande'],
    espagne: ['espagne'],
  };
  const REGION_CATEGORY_ORDER = Object.keys(REGION_KEYWORDS);

  function getRegionCategory(region) {
    const r = cleanStr(region);
    const hit = REGION_CATEGORY_ORDER.find(function(cat) {
      return REGION_KEYWORDS[cat].some(function(kw) { return r.includes(kw); });
    });
    return hit || 'monde';
  }

  // ---- FETCH STOREFRONT API ----
  function buildQuery(cursor) {
    const budget = state.answers.budget;
    const b = BUDGET_MAP[budget] || { min: 20, max: 99999 };
    const searchQuery = 'available_for_sale:true AND variants.price:>=' + (b.min / 1.2) + ' AND variants.price:<=' + (b.max * 1.2);
    const after = cursor ? ', after: "' + cursor + '"' : '';

    return '{\n' +
      '  products(first: 250, query: "' + searchQuery + '"' + after + ') {\n' +
      '    pageInfo { hasNextPage endCursor }\n' +
      '    edges { node {\n' +
      '      id title handle productType availableForSale tags\n' +
      '      featuredImage { url altText }\n' +
      '      variants(first: 10) { edges { node { id title availableForSale price { amount } compareAtPrice { amount } } } }\n' +
      '      metafields(identifiers: [\n' +
      '        {namespace:"custom",key:"color"},\n' +
      '        {namespace:"custom",key:"region"},\n' +
      '        {namespace:"custom",key:"apogee_min"},\n' +
      '        {namespace:"custom",key:"apogee_max"},\n' +
      '        {namespace:"custom",key:"vintage"},\n' +
      '        {namespace:"custom",key:"parker_wa"},\n' +
      '        {namespace:"custom",key:"james_suckling"},\n' +
      '        {namespace:"custom",key:"vinous"},\n' +
      '        {namespace:"custom",key:"jean_marc_quarin"}\n' +
      '      ]) { key value }\n' +
      '    }}\n' +
      '  }\n' +
      '}';
  }

  const MAX_FETCH_PAGES = 40; // 40 * 250 = 10 000 produits max par simulation
  const FETCH_TIMEOUT_MS = 15000;
  const FETCH_MAX_ATTEMPTS = 4; // 1 essai + 3 retentatives

  function wait(ms) {
    return new Promise(function(resolve) { setTimeout(resolve, ms); });
  }

  // Requête Storefront API avec timeout + retentatives (backoff) sur les pannes transitoires
  // (réseau, timeout, 429 rate-limit, 5xx) : le token public peut être limité en débit lors
  // d'un pic de trafic grand public, il ne faut pas planter toute la simulation sur un seul essai.
  async function fetchStorefrontGraphQL(query, attempt) {
    attempt = attempt || 1;
    const controller = new AbortController();
    const timeoutId = setTimeout(function() { controller.abort(); }, FETCH_TIMEOUT_MS);
    let res;

    try {
      res = await fetch(
        'https://' + CONFIG.shopDomain + '/api/' + CONFIG.apiVersion + '/graphql.json',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': CONFIG.storefrontToken,
          },
          body: JSON.stringify({ query: query }),
          signal: controller.signal,
        }
      );
    } catch (networkErr) {
      clearTimeout(timeoutId);
      const isTimeout = networkErr && networkErr.name === 'AbortError';
      if (attempt < FETCH_MAX_ATTEMPTS) {
        const backoff = 700 * Math.pow(2, attempt - 1);
        debugPanel('⚠ ' + (isTimeout ? 'Délai dépassé' : 'Erreur réseau') + ' sur le catalogue, nouvel essai dans ' + backoff + 'ms (' + attempt + '/' + FETCH_MAX_ATTEMPTS + ')');
        await wait(backoff);
        return fetchStorefrontGraphQL(query, attempt + 1);
      }
      throw new Error('SERVICE_INDISPONIBLE');
    }
    clearTimeout(timeoutId);

    if (res.status === 429 || res.status >= 500) {
      if (attempt < FETCH_MAX_ATTEMPTS) {
        const retryAfter = parseFloat(res.headers.get('Retry-After'));
        const backoff = retryAfter ? retryAfter * 1000 : 700 * Math.pow(2, attempt - 1);
        debugPanel('⚠ Catalogue indisponible (HTTP ' + res.status + '), nouvel essai dans ' + Math.round(backoff) + 'ms (' + attempt + '/' + FETCH_MAX_ATTEMPTS + ')');
        await wait(backoff);
        return fetchStorefrontGraphQL(query, attempt + 1);
      }
      throw new Error('SERVICE_INDISPONIBLE');
    }

    if (!res.ok) throw new Error('API HTTP ' + res.status);
    const json = await res.json();
    if (json.errors) throw new Error('GraphQL: ' + json.errors.map(function(e) { return e.message; }).join(', '));
    return json;
  }

  async function fetchProducts() {
    let all = [], cursor = null, hasNext = true, page = 0, rawCount = 0, excludedByPack = 0;
    showLoader(true);

    const budget = state.answers.budget;
    const b = BUDGET_MAP[budget] || { min: 20, max: 99999 };
    debugPanel('=== RECHERCHE CATALOGUE ===');
    debugPanel('Tranche budget choisie : ' + budget);
    debugPanel('Prix unitaire accepté (avec tolérance ±20%) : ' + (b.min / 1.2).toFixed(2) + '€ à ' + (b.max * 1.2).toFixed(2) + '€ / bouteille');
    debugPanel('Formats acceptés : ' + ((state.answers.quantite && state.answers.quantite.length) ? state.answers.quantite.map(function(f) { return 'x' + f; }).join(', ') : 'x6 (défaut)'));

    while (hasNext && page < MAX_FETCH_PAGES) {
      const json = await fetchStorefrontGraphQL(buildQuery(cursor));
      const pd = (json && json.data) ? json.data.products : null;
      if (!pd) break;

      rawCount += pd.edges.length;
      const nodes = pd.edges.map(function(e) { return normalize(e.node); }).filter(function(p) { return p.available; });
      excludedByPack += pd.edges.length - nodes.length;
      all = all.concat(nodes);

      debugPanel('Page ' + (page + 1) + ' : ' + pd.edges.length + ' produit(s) reçu(s), hasNextPage=' + pd.pageInfo.hasNextPage);

      hasNext = pd.pageInfo.hasNextPage;
      cursor = pd.pageInfo.endCursor;
      page++;
    }

    if (hasNext) {
      ERR('fetchProducts: limite de ' + MAX_FETCH_PAGES + ' pages atteinte, des produits éligibles n\'ont pas été récupérés. Augmenter MAX_FETCH_PAGES.');
      debugPanel('⚠ Limite de ' + MAX_FETCH_PAGES + ' pages atteinte — certains produits éligibles n\'ont pas été récupérés.');
    }

    debugPanel('Pages Shopify récupérées : ' + page + ' / ' + MAX_FETCH_PAGES);
    debugPanel('Produits renvoyés par Shopify (dans la fourchette de prix) : ' + rawCount);
    debugPanel('Produits exclus (aucun format sélectionné en stock) : ' + excludedByPack);
    debugPanel('Produits disponibles pour le scoring : ' + all.length);

    showLoader(false);
    return all;
  }

  function parseCriticNote(raw) {
    if (!raw) return null;
    const numbers = raw.match(/\d+(\.\d+)?/g);
    if (!numbers || !numbers.length) return null;
    return Math.max.apply(Math, numbers.map(Number)) || null;
  }

  function normalize(node) {
    const mf = {};
    const metafields = node.metafields || [];
    metafields.forEach(function(m) { if (m) mf[m.key] = m.value; });

    const allowedFormats = (state.answers.quantite && state.answers.quantite.length) ? state.answers.quantite : ['6'];
    const variantEdges = (node.variants && node.variants.edges) ? node.variants.edges : [];
    const matchedVariants = getMatchingVariants(variantEdges, allowedFormats);
    const sizes = Object.keys(matchedVariants);

    if (!sizes.length) {
      return { id: node.id, available: false };
    }

    const variants = sizes.map(function(size) {
      return {
        packSize: parseInt(size, 10),
        price: matchedVariants[size].price,
        compareAtPrice: matchedVariants[size].compareAtPrice,
        variantTitle: matchedVariants[size].variant.title,
        variantId: matchedVariants[size].variant.id,
      };
    });

    const imageUrl = (node.featuredImage && node.featuredImage.url) ? node.featuredImage.url : '';

    return {
      id: node.id,
      title: node.title,
      handle: node.handle,
      productType: node.productType || '',
      available: true,
      tags: node.tags || [],
      image: imageUrl,
      variants: variants,
      color: (mf.color || '').toLowerCase().trim(),
      region: (mf.region || '').toLowerCase().trim(),
      apogeeMin: parseInt(mf.apogee_min) || null,
      apogeeMax: parseInt(mf.apogee_max) || null,
      vintage: parseInt(mf.vintage) || null,
      scores: {
        parker: parseCriticNote(mf.parker_wa),
        suckling: parseCriticNote(mf.james_suckling),
        vinous: parseCriticNote(mf.vinous),
        quarin: parseCriticNote(mf.jean_marc_quarin),
      },
    };
  }

  // ---- SCORING & ALGORITHME ----
  function scoreProduct(p) {
    const budget = state.answers.budget;
    const regions = state.answers.regions;

    if (!p.available) return { score: null, reason: 'non disponible' };
    if (isSpiritueux(p)) return { score: null, reason: 'spiritueux exclu' };
    if (!p.apogeeMin && !p.apogeeMax) return { score: null, reason: 'apogée non renseignée' };
    if (getProductColorCategory(p) === 'autre') return { score: null, reason: 'couleur non reconnue' };

    const b = BUDGET_MAP[budget] || { min: 20, max: 99999 };
    const eligibleVariants = (p.variants || []).filter(function(v) {
      const unitPrice = v.packSize > 0 ? (v.price / v.packSize) : v.price;
      return unitPrice >= b.min / 1.2 && unitPrice <= b.max * 1.2;
    });

    if (!eligibleVariants.length) {
      return { score: null, reason: 'aucun format sélectionné dans la tranche de budget' };
    }

    let s = 10;

    if (regions.length > 0 && p.region) {
      if (regions.includes(getRegionCategory(p.region))) { s += 30; }
    }

    const critScore = scoreCritiques(p.scores);
    if (critScore) { s += critScore; }

    // Boost promo uniquement si TOUTES les variantes éligibles sont soldées : garantit que la
    // variante finalement retenue par resolveVariantForProduct() sera bien affichée en promo.
    const allEligibleOnPromo = eligibleVariants.every(function(v) { return v.compareAtPrice && v.compareAtPrice > v.price; });
    if (allEligibleOnPromo) { s += 10; }

    return { score: s, eligibleVariants: eligibleVariants };
  }

  function isSpiritueux(p) {
    const spiritueux = [
      'whisky', 'whiskey', 'rhum', 'rum', 'rhum agricole', 'gin', 'vodka',
      'tequila', 'mezcal', 'cognac', 'armagnac', 'calvados', 'brandy',
      'eau-de-vie', 'eau de vie', 'grappa', 'liqueur', 'liqueurs', 'pastis',
      'absinthe', 'baijiu', 'pisco', 'bourbon', 'single malt', 'spiritueux'
    ];
    const haystack = [p.productType, p.title].concat(p.tags || []).map(cleanStr).join(' ');
    return spiritueux.some(function(term) { return haystack.includes(term); });
  }

  // Vins "Collection" — réservés aux clients privilégiés, repérés par le tag Shopify exact
  // "Collection" (vérifié via l'API Storefront sur un produit réel). Ils restent inclus
  // normalement dans la simulation, juste signalés visuellement + bloqués côté ajout panier.
  function isCollectionProduct(p) {
    return (p.tags || []).some(function(tag) { return cleanStr(tag) === 'collection'; });
  }

  // Posé côté Liquid (bloc Custom Liquid) sur #sim-wrapper via data-collection-access, à
  // partir du metafield client custom.status === "COLLECTION" — un client non connecté ou
  // sans ce statut ne peut pas ajouter les vins Collection à son panier.
  function customerHasCollectionAccess() {
    const el = document.getElementById('sim-wrapper');
    return !!el && el.dataset.collectionAccess === 'true';
  }

  function scoreCritiques(sc) {
    const notes = Object.values(sc).filter(function(n) { return n !== null; });
    if (!notes.length) return 0;
    const best = Math.max.apply(Math, notes);
    return Math.max(0, Math.min(15, best - 85));
  }

  function assignHorizon(p) {
    const y = CONFIG.currentYear;
    const mn = p.apogeeMin || y;
    const mx = p.apogeeMax || y + 5;

    if (mn >= y + 10 || mx >= y + 15) return 'long';
    if (mn >= y + 4 || mx >= y + 8) return 'moyen';
    return 'court';
  }

  function calculateQuotas(totalAmount, percentages) {
    const keys = Object.keys(percentages);
    let activeKeys = keys.filter(function(k) { return percentages[k] > 0; });
    if (activeKeys.length === 0) {
      var firstObj = {};
      firstObj[keys[0]] = totalAmount;
      return firstObj;
    }

    let rawCounts = {};
    let currentSum = 0;

    keys.forEach(function(k) {
      if (percentages[k] > 0) {
        rawCounts[k] = Math.floor(totalAmount * (percentages[k] / 100));
        currentSum += rawCounts[k];
      } else {
        rawCounts[k] = 0;
      }
    });

    let remainder = totalAmount - currentSum;
    if (remainder > 0) {
      const sortedByPct = activeKeys.slice().sort(function(a, b) { return percentages[b] - percentages[a]; });
      let i = 0;
      while (remainder > 0) {
        rawCounts[sortedByPct[i % sortedByPct.length]]++;
        remainder--;
        i++;
      }
    }
    return rawCounts;
  }

  // Répartition du catalogue en stock par région (relevée via audit console le 26/08/2026),
  // utilisée pour diversifier automatiquement quand aucune région n'est choisie explicitement.
  const REGION_STOCK_PERCENTAGES = {
    bordeaux: 28.3, bourgogne: 21.0, rhone: 10.4, loire: 3.7, languedoc: 3.5,
    champagne: 6.5, beaujolais: 1.8, provence: 1.5, autresfr: 2.3,
    italie: 7.9, usa: 3.5, australie: 2.9, nouvellezelande: 0.8, espagne: 0.5,
    monde: 5.4,
  };

  function selectByRegionDistribution(candidates, quota, sharedCredit) {
    const categories = Object.keys(REGION_STOCK_PERCENTAGES);
    const pools = {};
    categories.forEach(function(c) { pools[c] = []; });

    candidates.forEach(function(p) {
      pools[getRegionCategory(p.region)].push(p);
    });
    categories.forEach(function(c) {
      pools[c].sort(function(a, b) { return b.score - a.score; });
    });

    // Tour de rôle pondéré (algorithme "surplus round robin") : à chaque tour, chaque région
    // accumule un crédit proportionnel à son poids, et on pioche dans celle qui a le plus de
    // crédit accumulé. Contrairement à un simple ratio cursor/quota, ça respecte les poids dès
    // les tout premiers picks. Le crédit est PARTAGÉ (sharedCredit) entre tous les appels d'une
    // même simulation (un appel par combinaison couleur × horizon) : sans ça, la région la plus
    // lourde (Bordeaux) gagnerait systématiquement le premier pick de CHAQUE sous-groupe, ce qui
    // la sur-représente globalement même si chaque sous-groupe est individuellement correct.
    const totalWeight = categories.reduce(function(sum, c) { return sum + REGION_STOCK_PERCENTAGES[c]; }, 0);
    const credit = sharedCredit || (function() {
      const c = {};
      categories.forEach(function(k) { c[k] = 0; });
      return c;
    })();
    const cursors = {};
    categories.forEach(function(c) { cursors[c] = 0; });

    const selected = [];
    const usedIds = new Set();
    const eligible = categories.filter(function(c) { return pools[c].length > 0; });

    while (selected.length < quota && eligible.some(function(c) { return cursors[c] < pools[c].length; })) {
      eligible.forEach(function(c) { credit[c] += REGION_STOCK_PERCENTAGES[c]; });
      let bestCat = null, bestCredit = -Infinity;
      eligible.forEach(function(c) {
        if (cursors[c] >= pools[c].length) return;
        if (credit[c] > bestCredit) { bestCredit = credit[c]; bestCat = c; }
      });
      if (!bestCat) break;
      const p = pools[bestCat][cursors[bestCat]];
      cursors[bestCat]++;
      selected.push(p);
      usedIds.add(p.id);
      credit[bestCat] -= totalWeight;
    }

    if (selected.length < quota) {
      const remaining = candidates
        .filter(function(p) { return !usedIds.has(p.id); })
        .sort(function(a, b) { return b.score - a.score; });
      const needed = quota - selected.length;
      remaining.slice(0, needed).forEach(function(p) { selected.push(p); });
    }

    return selected;
  }

  function selectBalancedPool(candidates, quota, userRegions, sharedRegionCredit) {
    if (quota <= 0 || !candidates.length) return [];

    if (!userRegions || userRegions.length === 0) {
      return selectByRegionDistribution(candidates, quota, sharedRegionCredit);
    }

    if (userRegions.length === 1) {
      return candidates.sort(function(a, b) { return b.score - a.score; }).slice(0, quota);
    }

    const regionPools = {};
    userRegions.forEach(function(r) { regionPools[r] = []; });
    regionPools['autre'] = [];

    candidates.forEach(function(p) {
      const cat = getRegionCategory(p.region);
      const matchedRegion = userRegions.includes(cat) ? cat : null;
      if (matchedRegion) {
        regionPools[matchedRegion].push(p);
      } else {
        regionPools['autre'].push(p);
      }
    });

    Object.keys(regionPools).forEach(function(rk) {
      regionPools[rk].sort(function(a, b) { return b.score - a.score; });
    });

    const selected = [];
    const usedIds = new Set();
    let added = true;

    while (selected.length < quota && added) {
      added = false;
      for (let i = 0; i < userRegions.length; i++) {
        const r = userRegions[i];
        if (selected.length >= quota) break;
        const pool = regionPools[r];
        const nextWine = pool.find(function(p) { return !usedIds.has(p.id); });
        if (nextWine) {
          usedIds.add(nextWine.id);
          selected.push(nextWine);
          added = true;
        }
      }
    }

    if (selected.length < quota) {
      const remaining = candidates
        .filter(function(p) { return !usedIds.has(p.id); })
        .sort(function(a, b) { return b.score - a.score; });

      const needed = quota - selected.length;
      remaining.slice(0, needed).forEach(function(p) { selected.push(p); });
    }

    return selected;
  }

  function buildIdealCellar(allScored) {
    const capacite = state.answers.capacite;
    const projet = state.answers.projet;
    const couleurs = state.answers.couleurs;
    const regions = state.answers.regions;
    const budgetMax = state.answers.budgetMax;
    const targetTotalBottles = CAPACITE_MAP[capacite] || 100;

    const cPct = getCouleursPercentages(couleurs);
    const colorBottleQuotas = calculateQuotas(targetTotalBottles, cPct);
    const horizonPct = getHorizonPercentages(projet);
    const horizonBottleQuotas = calculateQuotas(targetTotalBottles, horizonPct);

    debugPanel('=== RÉPARTITION COULEURS ===');
    debugPanel('Couleurs choisies : ' + (couleurs.length ? couleurs.join(', ') : 'aucune (défaut rouge 100%)'));
    debugPanel('Pourcentages appliqués : ' + Object.keys(cPct).map(function(k) { return k + ' ' + cPct[k] + '%'; }).join(' / '));
    debugPanel('Quotas bouteilles (objectif ' + targetTotalBottles + ' au total) : ' + Object.keys(colorBottleQuotas).map(function(k) { return k + ' ' + colorBottleQuotas[k]; }).join(' / '));

    debugPanel('=== RÉPARTITION HORIZONS DE GARDE ===');
    debugPanel('Profil : ' + (projet && projet.length ? projet.join(', ') : 'occasions (défaut)') + ' → court ' + horizonPct.court + '% / moyen ' + horizonPct.moyen + '% / long ' + horizonPct.long + '%');
    debugPanel('Quotas bouteilles : court ' + horizonBottleQuotas.court + ' / moyen ' + horizonBottleQuotas.moyen + ' / long ' + horizonBottleQuotas.long);

    const selectedFormats = (state.answers.quantite && state.answers.quantite.length) ? state.answers.quantite : ['6'];
    const formatPct = getFormatsPercentages(selectedFormats);
    const formatBottleQuotas = calculateQuotas(targetTotalBottles, formatPct);
    const remainingFormatQuota = Object.assign({}, formatBottleQuotas);

    debugPanel('=== RÉPARTITION FORMATS DE CONDITIONNEMENT ===');
    debugPanel('Formats choisis : ' + selectedFormats.map(function(f) { return 'x' + f; }).join(', '));
    debugPanel('Pondération appliquée : ' + Object.keys(formatPct).map(function(k) { return 'x' + k + ' ' + formatPct[k] + '%'; }).join(' / '));
    debugPanel('Quotas bouteilles : ' + Object.keys(formatBottleQuotas).map(function(k) { return 'x' + k + ' ' + formatBottleQuotas[k]; }).join(' / '));

    // Crédit partagé entre les appels à selectByRegionDistribution d'un MÊME horizon (un appel
    // par couleur), remis à zéro à chaque nouvel horizon — pour que chaque horizon (court/moyen/
    // long) reflète individuellement les pourcentages cibles, plutôt qu'une diversification
    // seulement au niveau de la cave entière.
    function freshRegionCredit() {
      const c = {};
      Object.keys(REGION_STOCK_PERCENTAGES).forEach(function(k) { c[k] = 0; });
      return c;
    }

    debugPanel('=== RÉPARTITION RÉGIONS ===');
    if (regions.length === 0) {
      debugPanel('Régions choisies : aucune → répartition automatique proportionnelle au stock catalogue');
      debugPanel('Pourcentages appliqués : ' + Object.keys(REGION_STOCK_PERCENTAGES).map(function(k) { return k + ' ' + REGION_STOCK_PERCENTAGES[k] + '%'; }).join(' / '));
    } else if (regions.length === 1) {
      debugPanel('Région choisie : ' + regions[0] + ' → priorité au score, pas de diversification (une seule région)');
    } else {
      debugPanel('Régions choisies : ' + regions.join(', ') + ' → répartition en tour de rôle (round-robin) entre elles');
    }

    function resolveVariantForProduct(p) {
      const candidates = p.eligibleVariants || [];
      if (!candidates.length) return null;
      return candidates.slice().sort(function(a, b) {
        return (remainingFormatQuota[String(b.packSize)] || 0) - (remainingFormatQuota[String(a.packSize)] || 0);
      })[0];
    }

    const candidatesByColor = { rouge: [], blanc: [], rose: [], champagne: [], autre: [] };
    allScored.forEach(function(p) {
      const cat = getProductColorCategory(p);
      candidatesByColor[cat].push(p);
    });

    debugPanel('=== CANDIDATS DISPONIBLES PAR COULEUR (après scoring, avant remplissage) ===');
    Object.keys(candidatesByColor).forEach(function(k) {
      debugPanel(k + ' : ' + candidatesByColor[k].length + ' vin(s) disponible(s)');
    });

    const selected = [];
    const usedIds = new Set();
    let currentTotalPrice = 0;

    // Construit, pour un horizon donné, une fonction addOneBottle() qui ajoute AU PLUS une
    // bouteille à chaque appel (même ordre de priorité qu'avant : quotas couleur de cet
    // horizon d'abord, puis file de secours toutes couleurs confondues triée par score) et
    // renvoie false quand l'horizon n'a plus rien à ajouter (quota atteint ou plus aucun
    // candidat exploitable / budget épuisé). Le découpage "une bouteille à la fois" permet de
    // faire tourner les horizons en tour de rôle (voir plus bas) au lieu de vider le budget
    // max sur le premier horizon traité avant que les suivants n'aient eu leur chance.
    function buildHorizonFiller(horizonKey, horizonTarget) {
      const regionCredit = freshRegionCredit();
      const colorKeys = Object.keys(colorBottleQuotas).filter(function(k) { return colorBottleQuotas[k] > 0; });
      const colorTargets = {};
      const colorPools = {};
      colorKeys.forEach(function(colKey) {
        colorTargets[colKey] = Math.round(horizonTarget * (colorBottleQuotas[colKey] / targetTotalBottles));
        const pool = candidatesByColor[colKey].filter(function(p) { return p.horizon === horizonKey; });
        colorPools[colKey] = selectBalancedPool(pool, pool.length, regions, regionCredit);
      });

      let fallbackPool = null;
      function getFallbackPool() {
        if (!fallbackPool) {
          fallbackPool = allScored
            .filter(function(p) {
              return p.horizon === horizonKey &&
                colorKeys.some(function(colKey) { return getProductColorCategory(p) === colKey; });
            })
            .sort(function(a, b) { return b.score - a.score; });
        }
        return fallbackPool;
      }

      let colorIdx = 0;
      let poolIdx = 0;
      let colorBottles = 0;
      let horizonBottles = 0;
      let fallbackIdx = 0;
      let phase = 'colors';

      function addResolved(p, variant) {
        const resolved = Object.assign({}, p, { packSize: variant.packSize, price: variant.price, compareAtPrice: variant.compareAtPrice, variantTitle: variant.variantTitle, variantId: variant.variantId });
        usedIds.add(p.id);
        selected.push(resolved);
        currentTotalPrice += resolved.price;
        remainingFormatQuota[String(resolved.packSize)] = (remainingFormatQuota[String(resolved.packSize)] || 0) - resolved.packSize;
        horizonBottles += resolved.packSize;
        return resolved;
      }

      return function addOneBottle() {
        if (horizonBottles >= horizonTarget) return false;

        while (phase === 'colors' && colorIdx < colorKeys.length) {
          const colKey = colorKeys[colorIdx];
          if (colorBottles >= colorTargets[colKey]) {
            colorIdx++; colorBottles = 0; poolIdx = 0;
            continue;
          }
          const pool = colorPools[colKey];
          while (poolIdx < pool.length) {
            const p = pool[poolIdx];
            poolIdx++;
            if (usedIds.has(p.id)) continue;
            const variant = resolveVariantForProduct(p);
            if (!variant) continue;
            if (budgetMax && (currentTotalPrice + variant.price > budgetMax)) continue;

            const resolved = addResolved(p, variant);
            colorBottles += resolved.packSize;
            return true;
          }
          colorIdx++; colorBottles = 0; poolIdx = 0;
        }

        phase = 'fallback';
        const fbPool = getFallbackPool();
        while (fallbackIdx < fbPool.length) {
          const p = fbPool[fallbackIdx];
          fallbackIdx++;
          if (usedIds.has(p.id)) continue;
          const variant = resolveVariantForProduct(p);
          if (!variant) continue;
          if (budgetMax && (currentTotalPrice + variant.price > budgetMax)) continue;

          addResolved(p, variant);
          return true;
        }

        return false;
      };
    }

    const activeHorizonKeys = Object.keys(horizonBottleQuotas).filter(function(k) { return horizonBottleQuotas[k] > 0; });
    const horizonFillers = {};
    activeHorizonKeys.forEach(function(k) { horizonFillers[k] = buildHorizonFiller(k, horizonBottleQuotas[k]); });

    // Remplissage en tour de rôle : une bouteille pour chaque horizon actif à chaque
    // passage, jusqu'à ce qu'aucun horizon ne puisse plus rien ajouter. Avant, les horizons
    // étaient remplis en séquence (court → moyen → long) : avec un budget max serré, le
    // premier horizon traité consommait tout le budget et les suivants se retrouvaient à 0,
    // donnant une cave 100% "à boire vite" au lieu du mix court/moyen/long attendu.
    const stillActive = new Set(activeHorizonKeys);
    while (stillActive.size > 0) {
      activeHorizonKeys.forEach(function(k) {
        if (!stillActive.has(k)) return;
        if (!horizonFillers[k]()) stillActive.delete(k);
      });
    }

    let currentTotalBottles = selected.reduce(function(sum, p) { return sum + p.packSize; }, 0);
    if (currentTotalBottles < targetTotalBottles) {
      const allowedCategories = new Set(
        Object.keys(colorBottleQuotas).filter(function(k) { return colorBottleQuotas[k] > 0; })
      );

      const remainingPool = allScored.filter(function(p) {
        if (usedIds.has(p.id)) return false;
        const cat = getProductColorCategory(p);
        return allowedCategories.has(cat);
      });

      for (let j = 0; j < remainingPool.length; j++) {
        const p = remainingPool[j];
        if (currentTotalBottles >= targetTotalBottles) break;

        const variant = resolveVariantForProduct(p);
        if (!variant) continue;
        if (budgetMax && (currentTotalPrice + variant.price > budgetMax)) {
          continue;
        }

        const resolved = Object.assign({}, p, { packSize: variant.packSize, price: variant.price, compareAtPrice: variant.compareAtPrice, variantTitle: variant.variantTitle, variantId: variant.variantId });
        usedIds.add(p.id);
        selected.push(resolved);
        currentTotalBottles += resolved.packSize;
        currentTotalPrice += resolved.price;
        remainingFormatQuota[String(resolved.packSize)] = (remainingFormatQuota[String(resolved.packSize)] || 0) - resolved.packSize;
      }
    }

    debugPanel('=== SÉLECTION FINALE PAR HORIZON ===');
    Object.keys(horizonBottleQuotas).forEach(function(horizonKey) {
      const group = selected.filter(function(p) { return p.horizon === horizonKey; });
      const bottles = group.reduce(function(sum, p) { return sum + p.packSize; }, 0);
      const byColor = {};
      group.forEach(function(p) {
        const cat = getProductColorCategory(p);
        byColor[cat] = (byColor[cat] || 0) + p.packSize;
      });
      const detail = Object.keys(byColor).map(function(k) { return k + ':' + byColor[k]; }).join(' ') || 'aucun';
      debugPanel(horizonKey + ' : ' + bottles + ' / ' + horizonBottleQuotas[horizonKey] + ' bouteilles visées (' + detail + ')');

      const byRegion = {};
      group.forEach(function(p) {
        const cat = getRegionCategory(p.region);
        byRegion[cat] = (byRegion[cat] || 0) + p.packSize;
      });
      const regionDetail = Object.keys(byRegion)
        .sort(function(a, b) { return byRegion[b] - byRegion[a]; })
        .map(function(k) { return k + ':' + byRegion[k] + ' (' + Math.round((byRegion[k] / (bottles || 1)) * 100) + '%)'; })
        .join(' / ') || 'aucun';
      debugPanel('  ↳ régions : ' + regionDetail);
    });
    debugPanel('TOTAL : ' + selected.reduce(function(sum, p) { return sum + p.packSize; }, 0) + ' / ' + targetTotalBottles + ' bouteilles, ' + selected.length + ' références');

    debugPanel('=== SÉLECTION FINALE PAR FORMAT ===');
    Object.keys(formatBottleQuotas).forEach(function(size) {
      const group = selected.filter(function(p) { return String(p.packSize) === size; });
      const bottles = group.reduce(function(sum, p) { return sum + p.packSize; }, 0);
      debugPanel('x' + size + ' : ' + bottles + ' / ' + formatBottleQuotas[size] + ' bouteilles visées (' + group.length + ' référence(s))');
    });

    debugPanel('=== SÉLECTION FINALE PAR RÉGION ===');
    const finalByRegion = {};
    selected.forEach(function(p) {
      const cat = getRegionCategory(p.region);
      finalByRegion[cat] = (finalByRegion[cat] || 0) + p.packSize;
    });
    Object.keys(finalByRegion).sort(function(a, b) { return finalByRegion[b] - finalByRegion[a]; }).forEach(function(cat) {
      const pct = Math.round((finalByRegion[cat] / (selected.reduce(function(sum, p) { return sum + p.packSize; }, 0) || 1)) * 100);
      debugPanel(cat + ' : ' + finalByRegion[cat] + ' bouteille(s) (' + pct + '% du total)');
    });

    return selected;
  }

  function getRecapItems() {
    const a = state.answers;

    const LABELS = {
      projet: {
        'plaisir': '🍷 Plaisir quotidien',
        'occasions': '✨ Grandes occasions',
        'patrimoine': '🏛️ Patrimoine & transmission'
      },
      situation: {
        'zero': 'Je pars de zéro',
        'quelques': 'Quelques bouteilles',
        'completer': 'Cave à compléter',
        'restructurer': 'Restructurer ma cave'
      },
      capacite: {
        'lt50': '30 à 50 bouteilles',
        '50-150': '50 à 150 bouteilles',
        '150-500': '150 à 300 bouteilles',
        'gt500': 'Plus de 300 bouteilles'
      },
      budget: {
        '20-50': '20€ – 50€ / btl',
        '50-80': '50€ – 80€ / btl',
        '80-120': '80€ – 120€ / btl',
        'gt120': '> 120€ / btl'
      },
      quantite: {
        '1': 'x1',
        '3': 'x3',
        '6': 'x6'
      },
      regions: {
        'bordeaux': 'Bordeaux',
        'bourgogne': 'Bourgogne',
        'rhone': 'Rhône',
        'loire': 'Loire',
        'languedoc': 'Languedoc',
        'champagne': 'Champagne',
        'beaujolais': 'Beaujolais',
        'provence': 'Provence',
        'autresfr': 'Autres régions françaises',
        'italie': 'Italie',
        'usa': 'USA',
        'australie': 'Australie',
        'nouvellezelande': 'Nouvelle-Zélande',
        'espagne': 'Espagne',
        'monde': 'Autres régions du monde'
      },
      formats: {
        'standard': 'Bouteille (75cl)',
        'demi': 'Demi-bouteille (37,5cl)',
        'magnum': 'Magnum (150cl)',
        'jeroboam': 'Jéroboam (300cl)',
        'imperiale': 'Impériale (600cl)'
      }
    };

    const formatArr = function(arr) {
      if (!arr || !arr.length) return 'Aucun';
      return arr.map(function(s) { return s.charAt(0).toUpperCase() + s.slice(1); }).join(', ');
    };

    const formatByLabels = function(arr, dict) {
      if (!arr || !arr.length) return 'Aucun';
      return arr.map(function(s) { return dict[s] || s; }).join(', ');
    };

    return [
      { label: 'Objectifs', val: formatByLabels(a.projet, LABELS.projet) },
      { label: 'Situation', val: LABELS.situation[a.situation] || '-' },
      { label: 'Capacité cible', val: LABELS.capacite[a.capacite] || '-' },
      { label: 'Budget / btl', val: LABELS.budget[a.budget] || '-' },
      { label: 'Budget max cave', val: a.budgetMax ? a.budgetMax + ' €' : 'Non défini' },
      { label: 'Couleurs', val: formatArr(a.couleurs) },
      { label: 'Régions', val: formatByLabels(a.regions, LABELS.regions) },
      { label: 'Conditionnement', val: formatByLabels(a.quantite, LABELS.quantite) },
      { label: 'Formats de bouteilles', val: formatByLabels(a.formats, LABELS.formats) }
    ];
  }

  // Même récap que getRecapItems(), mais sous forme de tableau {Critere, Valeur} directement
  // exploitable par un {% for %} Klaviyo (pas de split/assign nécessaire côté template — ces
  // deux tags ne sont pas supportés par le moteur Liquid de Klaviyo, voir les emails de notif).
  function getRecapItemsForKlaviyo() {
    return getRecapItems()
      .filter(function(item) { return item.val && item.val !== '-'; })
      .map(function(item) { return { Critere: item.label, Valeur: item.val }; });
  }

  function renderRecapHTML() {
    const recapItems = getRecapItems();

    const filledCriteria = recapItems.filter(function(item) {
      return item.val && item.val !== '- ' && item.val !== '-';
    }).length;

    let html = '<div class="sim-recap-box">' +
      '<button class="sim-recap-toggle" type="button" aria-expanded="false">' +
      '<span><strong>Récapitulatif de votre simulation</strong><em>' + filledCriteria + ' / ' + recapItems.length + ' critères remplis</em></span>' +
      '<span class="sim-recap-toggle-label">Voir mes critères <span aria-hidden="true">↓</span></span>' +
      '</button>' +
      '<div class="sim-recap-details" hidden>' +
      '<div class="sim-recap-grid">';

    recapItems.forEach(function(item) {
      html += '<div class="sim-recap-item">' +
        '<div class="sim-recap-label">' + item.label + '</div>' +
        '<div class="sim-recap-val">' + item.val + '</div>' +
        '</div>';
    });

    html += '</div></div></div>';
    return html;
  }

  function classifyRejection(reason) {
    if (!reason) return 'autre';
    if (reason === 'non disponible') return 'non disponible';
    if (reason === 'spiritueux exclu') return 'spiritueux';
    return reason;
  }

  // ---- PIPELINE DE CALCUL ----
  async function run() {
    try {
      const dbgEl = document.getElementById('sim-debug');
      if (dbgEl) dbgEl.textContent = '';

      debugPanel('=== DÉMARRAGE DE LA SIMULATION ===');
      state.products = await fetchProducts();
      // Si le fetch ne renvoie aucun produit, on laisse le flux continuer normalement :
      // scored/idealCellar resteront vides et renderResults() affichera l'écran "no result"
      // (avec sa notif Klaviyo dédiée) — c'est la même situation pour l'utilisateur que
      // "aucun vin ne correspond après scoring", pas la peine de la traiter différemment.

      const scored = [];
      const rejections = {};
      state.products.forEach(function(p) {
        const result = scoreProduct(p);
        if (result.score !== null) {
          scored.push(Object.assign({}, p, { score: result.score, horizon: assignHorizon(p), eligibleVariants: result.eligibleVariants }));
        } else {
          const cat = classifyRejection(result.reason);
          rejections[cat] = (rejections[cat] || 0) + 1;
        }
      });

      debugPanel('=== FILTRAGE PAR CRITÈRES (score, budget, exclusions) ===');
      debugPanel('Produits retenus (scorés) : ' + scored.length);
      Object.keys(rejections).forEach(function(cat) {
        debugPanel('Rejetés — ' + cat + ' : ' + rejections[cat]);
      });

      scored.sort(function(a, b) { return b.score - a.score; });

      const idealCellar = buildIdealCellar(scored);
      state.idealCellar = idealCellar;

      const totalBtl = idealCellar.reduce(function(sum, p) { return sum + p.packSize; }, 0);
      const totalCost = idealCellar.reduce(function(sum, p) { return sum + p.price; }, 0);
      debugPanel('=== RÉSULTAT FINAL ===');
      debugPanel('Total cave finale : ' + totalBtl + ' bouteilles (' + idealCellar.length + ' références) — ' + totalCost.toFixed(2) + ' €');

      renderResults(idealCellar);

    } catch (e) {
      ERR('Run error:', e);
      showError('Le catalogue est momentanément indisponible (forte affluence ou connexion instable). Merci de réessayer dans quelques instants.');
    }
  }

  const HORIZONS = [
    { key: 'court', label: 'À consommer (0–3 ans)' },
    { key: 'moyen', label: 'À faire vieillir (4–10 ans)' },
    { key: 'long',  label: 'Vin de longue garde (+10 ans)' },
  ];

  // ---- AJOUT AU PANIER (sélection via cases à cocher) ----
  // L'id de variante Shopify tracké tout du long est un GID GraphQL
  // ("gid://shopify/ProductVariant/123456789") — /cart/add.js veut l'id numérique final.
  function extractNumericVariantId(gid) {
    const m = String(gid || '').match(/(\d+)$/);
    return m ? m[1] : null;
  }

  // La barre "Ajouter au panier" est en position:fixed en permanence (voir CSS #sim-cart-insert)
  // — plusieurs approches conditionnelles (sticky natif, IntersectionObserver+sentinel, scroll
  // listener) se sont révélées peu fiables en conditions réelles sur cette boutique. Ce padding
  // réserve en bas de #simulateur-results-wrapper un petit espace pour que le dernier élément
  // (encadré "Laisser un avis") ne colle pas directement au bord de l'écran.
  //
  // Volontairement une petite valeur fixe (20px), PAS la hauteur réelle de la barre : la caler
  // sur cette hauteur a été essayé, mais ça laissait un grand vide sous l'encadré contact (la
  // barre étant assez haute) et, pire, tout recalcul dynamique de cette valeur déplaçait
  // visiblement le contenu SEO en dessous à chaque changement — gênant même avec une transition
  // douce.
  //
  // Appelée UNE SEULE FOIS (voir l'unique appel dans renderResults()) — jamais recalculée après
  // coup (ni au scroll, ni au resize, ni quand la barre s'efface/réapparaît ou change de
  // contenu) : le contenu en dessous ne doit plus jamais bouger pour cette raison.
  function syncResultsBottomPadding() {
    const wrapperEl = document.getElementById('simulateur-results-wrapper');
    if (!wrapperEl) return;
    wrapperEl.style.paddingBottom = '20px';
  }

  let cartEndObserver = null;

  // Efface en douceur la barre flottante une fois qu'on a vraiment dépassé la fin du bloc
  // résultats — repère : #sim-cart-end-sentinel, tout en bas de #simulateur-results-wrapper,
  // juste avant le contenu SEO.
  //
  // rootMargin négatif : sans ça, le sentinel ne "s'intersecte" qu'une fois son bord réellement
  // au ras du bas du viewport — la barre resterait visible par-dessus lui jusqu'au tout dernier
  // pixel, provoquant un effacement tardif et brusque. On rétrécit donc artificiellement la zone
  // de détection, de la hauteur RÉELLE et ACTUELLE de la barre (recalculée à chaque changement de
  // contenu via syncCartBarLayout()) plutôt que d'une valeur fixe devinée à l'avance — une marge
  // fixe se retrouve systématiquement trop petite ou trop grande selon que la barre affiche 1 ou
  // 2 lignes de texte promo, la notice Collection en mode sélection, etc., ce qui déclenchait le
  // fondu au mauvais moment (trop tôt, laissant un vide inutile ; ou trop tard, la barre
  // recouvrant encore le repère). Ici, le fondu se déclenche pile quand la barre commencerait à
  // recouvrir le repère de fin, quel que soit son contenu du moment.
  function syncCartEndObserver() {
    const insertEl = document.getElementById('sim-cart-insert');
    const sentinel = document.getElementById('sim-cart-end-sentinel');
    if (!insertEl || !sentinel || !('IntersectionObserver' in window)) return;

    if (cartEndObserver) cartEndObserver.disconnect();

    const margin = insertEl.offsetHeight || 0;

    cartEndObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        // entry.isIntersecting est ambigu ici : il redevient false aussi bien AVANT que le
        // repère atteigne la ligne de déclenchement (barre doit être visible, on n'a pas encore
        // fini de scroller) qu'APRÈS l'avoir dépassée en continuant à scroller vers le bas
        // (barre doit rester cachée, on a fini de scroller) — même valeur booléenne pour deux
        // situations opposées. C'est ce qui faisait réapparaître la barre une fois scrollé assez
        // loin dans le contenu SEO (confirmé en test réel : isIntersecting repasse à false quand
        // le repère sort du haut de la zone observée). On compare donc directement la position du
        // repère à la ligne de déclenchement (rootBounds.bottom, qui intègre déjà le rootMargin) :
        // caché dès que le repère atteint ou dépasse cette ligne, peu importe qu'on l'approche par
        // en dessous ou qu'on l'ait déjà dépassée.
        const shouldHide = entry.rootBounds
          ? (entry.boundingClientRect.top <= entry.rootBounds.bottom)
          : entry.isIntersecting;
        // Ne touche QUE la classe de la barre — jamais le padding réservé (voir
        // syncResultsBottomPadding(), figé une fois pour toutes au premier rendu des résultats).
        insertEl.classList.toggle('sim-cart-insert-hidden', shouldHide);
      });
    }, { rootMargin: '0px 0px -' + margin + 'px 0px', threshold: 0 });

    cartEndObserver.observe(sentinel);
  }

  // À appeler après tout changement pouvant modifier la hauteur de la barre fixed (nouveau
  // contenu, resize...) pour garder la marge de déclenchement de la disparition à jour. Ne touche
  // volontairement plus au padding réservé en bas de page (syncResultsBottomPadding, appelée une
  // seule fois ailleurs, voir plus haut) : le recalculer ici aurait fait bouger le contenu SEO en
  // dessous à chaque changement de hauteur de barre.
  function syncCartBarLayout() {
    syncCartEndObserver();
  }

  // Bloc unique fusionnant la bannière promo (si des vins retenus sont en promo — pas de
  // date affichée : plusieurs promos peuvent coexister avec des échéances différentes, on
  // reste volontairement vague sur la durée) et le déclencheur "Ajouter au panier".
  function renderCartInsert(products) {
    const el = document.getElementById('sim-cart-insert');
    if (!el) return;
    if (!products.length) { el.innerHTML = ''; syncCartBarLayout(); return; }

    const totalDiscount = products.reduce(function(sum, p) {
      return sum + ((p.compareAtPrice && p.compareAtPrice > p.price) ? (p.compareAtPrice - p.price) : 0);
    }, 0);
    const hasPromo = totalDiscount > 0;

    const textHtml = hasPromo
      ? '<div class="sim-cart-bar-promo-text">' +
        '<strong class="sim-cart-bar-promo-title">Votre sélection vous plaît ?</strong>' +
        '<span class="sim-cart-bar-promo">Profitez de <strong>' + fmt(totalDiscount) + '</strong> de réductions en commandant votre cave dès maintenant. <span class="sim-cart-bar-promo-nowrap"><span aria-hidden="true">⏳</span> Offres à durée limitée.</span></span>' +
        '</div>'
      : '<div class="sim-cart-bar-promo-text">' +
        '<strong class="sim-cart-bar-promo-title">Votre sélection est prête !</strong>' +
        '<span class="sim-cart-bar-promo sim-cart-bar-default-sub">Ajoutez ces vins au panier pour finaliser votre commande.</span>' +
        '</div>';

    el.innerHTML = '<div class="sim-cart-bar ' + (hasPromo ? 'sim-cart-bar-promo-active' : 'sim-cart-bar-default-active') + '">' +
      textHtml +
      '<button class="sim-btn sim-btn-primary sim-cart-start" type="button">Ajouter la sélection au panier</button>' +
      '</div>';
    syncCartBarLayout();
  }

  function updateCartBarCount() {
    const countEl = document.getElementById('sim-cart-count');
    if (!countEl) return;
    const checked = document.querySelectorAll('.sim-cart-check:checked');
    let bottles = 0;
    checked.forEach(function(cb) { bottles += parseInt(cb.dataset.packSize, 10) || 0; });
    const refs = checked.length;
    countEl.textContent = refs + ' référence' + (refs > 1 ? 's' : '') + ' · ' + bottles + ' bouteille' + (bottles > 1 ? 's' : '') + ' sélectionnée' + (refs > 1 ? 's' : '');
    const confirmBtn = document.querySelector('.sim-cart-confirm');
    if (confirmBtn) confirmBtn.disabled = (refs === 0);
  }

  function setCartSelectionMode(active) {
    const resultsEl = document.getElementById('simulateur-results');
    const barEl = document.getElementById('sim-cart-insert');
    if (!resultsEl || !barEl) return;
    resultsEl.classList.toggle('sim-cart-mode', active);

    if (active) {
      // disabled explicitement exclu : un checkbox disabled peut quand même matcher :checked
      // s'il est coché par script, ce qui l'aurait fait passer dans submitCartSelection().
      document.querySelectorAll('.sim-cart-check:not(:disabled)').forEach(function(cb) { cb.checked = true; });
      document.querySelectorAll('.sim-cart-check:disabled').forEach(function(cb) { cb.checked = false; });

      // Prévient explicitement le client si la sélection contient au moins un vin Collection
      // verrouillé (checkbox disabled) — sinon il ne comprend pas pourquoi cette référence
      // n'est pas cochable ni ajoutée au panier avec le reste.
      const hasLockedCollection = (state.idealCellar || []).some(function(p) {
        return isCollectionProduct(p) && !customerHasCollectionAccess();
      });
      const collectionNoticeHtml = hasLockedCollection
        ? '<p class="sim-cart-bar-collection-notice">🔒 Les vins <strong>Exclusivité Collection</strong> sont réservés à nos clients membres Collection. Contactez-nous au <a href="tel:+33788210563">+33 (0)7 88 21 05 63</a> pour en savoir plus.</p>'
        : '';

      barEl.innerHTML = '<div class="sim-cart-bar sim-cart-bar-active">' +
        '<p class="sim-cart-bar-heading">Toutes les références sont cochées par défaut — décochez celles que vous ne souhaitez pas commander.</p>' +
        collectionNoticeHtml +
        '<div class="sim-cart-bar-info">' +
        '<strong id="sim-cart-count"></strong>' +
        '<span class="sim-cart-bar-links">' +
        '<button class="sim-cart-link sim-cart-select-all" type="button">Tout cocher</button>' +
        '<button class="sim-cart-link sim-cart-select-none" type="button">Tout décocher</button>' +
        '</span>' +
        '</div>' +
        '<div class="sim-cart-bar-actions">' +
        '<button class="sim-btn sim-btn-ghost sim-cart-cancel" type="button">Annuler</button>' +
        '<button class="sim-btn sim-btn-primary sim-cart-confirm" type="button">Ajouter au panier</button>' +
        '</div>' +
        '</div>';
      updateCartBarCount();
      syncCartBarLayout();
    } else {
      renderCartInsert(state.idealCellar || []);
    }
  }

  function chunkArray(arr, size) {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  }

  async function submitCartSelection() {
    const confirmBtn = document.querySelector('.sim-cart-confirm');
    const checkedIds = new Set();
    document.querySelectorAll('.sim-cart-check:checked').forEach(function(cb) { checkedIds.add(cb.dataset.productId); });

    const selectedProducts = (state.idealCellar || []).filter(function(p) { return checkedIds.has(p.id); });
    const items = selectedProducts
      .map(function(p) { return { id: parseInt(extractNumericVariantId(p.variantId), 10), quantity: 1 }; })
      .filter(function(item) { return item.id; });

    if (!items.length) { alert('Aucun vin sélectionné.'); return; }

    if (confirmBtn) { confirmBtn.disabled = true; confirmBtn.textContent = 'Ajout en cours…'; }

    // Lots de 50 plutôt que 10 : le nombre d'allers-retours réseau séquentiels est ce qui
    // domine le temps d'attente perçu (chaque lot attend la réponse du précédent, par
    // conception, pour isoler les échecs partiels) — les regrouper par 50 au lieu de 10
    // divise ce temps par ~5 sans perdre la résilience lot par lot.
    const chunks = chunkArray(items, 50);
    let addedCount = 0;
    let failedCount = 0;

    for (let i = 0; i < chunks.length; i++) {
      if (confirmBtn && chunks.length > 1) {
        confirmBtn.textContent = 'Ajout en cours… (' + (i + 1) + '/' + chunks.length + ')';
      }
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: chunks[i] })
        });
        if (res.ok) {
          addedCount += chunks[i].length;
        } else {
          failedCount += chunks[i].length;
          ERR('Cart add: lot refusé', await res.text().catch(function() { return ''; }));
        }
      } catch (e) {
        failedCount += chunks[i].length;
        ERR('Cart add: erreur réseau', e);
      }
    }

    if (failedCount > 0) {
      alert(
        (addedCount > 0
          ? addedCount + ' référence(s) ajoutée(s) à votre panier. '
          : 'Aucune référence n\'a pu être ajoutée. ') +
        failedCount + ' référence(s) n\'ont pas pu être ajoutée(s) (stock insuffisant ou indisponible entre-temps).'
      );
    }

    if (addedCount > 0) {
      trackEvent('sim_cart_add', { items_count: addedCount });
      // Nouvel onglet plutôt qu'une redirection de la page courante : sinon l'utilisateur
      // perd sa simulation (résultats, sélection) en quittant la page pour aller sur /cart.
      window.open('/cart', '_blank');
      if (confirmBtn) { confirmBtn.disabled = false; confirmBtn.textContent = 'Ajouté au panier !'; }
      setCartSelectionMode(false);
    } else if (confirmBtn) {
      confirmBtn.disabled = false;
      confirmBtn.textContent = 'Ajouter au panier';
    }
  }

  // ---- RENDU SUR LE SITE (FRACTIONNEMENT COULEURS) ----
  function renderResults(products) {
    const container = document.getElementById('simulateur-results');
    const recapContainer = document.getElementById('sim-recap-container');
    if (!container) return;

    scrollSimToTop();

    const totalPrice = products.reduce(function(sum, p) { return sum + p.price; }, 0);
    const totalBottles = products.reduce(function(sum, p) { return sum + p.packSize; }, 0);
    const wineCount = new Set(products.map(function(p) { return p.handle || p.id; }).filter(Boolean)).size;

    container.innerHTML = '';

    if (recapContainer) {
      recapContainer.innerHTML = renderRecapHTML();
      // Attaché ici (juste après le rendu du récap, avant le "return" anticipé du cas "sans
      // résultat" un peu plus bas) plutôt qu'en bas de la fonction : le bouton "Voir mes
      // critères" est présent dans les deux cas (résultats ou non), mais le code en bas de
      // renderResults() n'est jamais atteint quand products.length === 0 à cause de ce
      // "return" — le clic restait silencieusement sans effet sur l'écran "aucun vin ne
      // correspond".
      const recapToggle = recapContainer.querySelector('.sim-recap-toggle');
      const recapDetails = recapContainer.querySelector('.sim-recap-details');
      if (recapToggle && recapDetails) {
        recapToggle.addEventListener('click', function() {
          const expanded = recapToggle.getAttribute('aria-expanded') === 'true';
          recapToggle.setAttribute('aria-expanded', String(!expanded));
          recapDetails.hidden = expanded;
        });
      }
    }

    if (products.length === 0) {
      trackEvent('sim_no_result', {});

      const summaryRow = document.querySelector('.sim-results-summary-row');
      if (summaryRow) summaryRow.style.display = 'none';
      const bottomActionsBox = document.querySelector('.sim-results-actions .sim-actions-box');
      if (bottomActionsBox) bottomActionsBox.style.display = 'none';
      renderCartInsert([]);

      sendMonitoringEvent('Simulateur_Sans_Resultat').catch(function() {});

      container.innerHTML = '<div class="sim-no-result">' +
        '<h3>Aucun vin ne correspond à ces critères</h3>' +
        '<p>Essayez d’élargir votre budget par bouteille, vos formats acceptés ou vos couleurs pour obtenir une sélection.</p>' +
        '<div class="sim-no-result-restart-wrap">' +
        '<button class="sim-btn sim-btn-ghost sim-no-result-restart" type="button">Recommencer la simulation</button>' +
        '</div>' +
        renderContactCta('sim-no-result-contact', 'Notre algorithme n\'a rien trouvé, mais nos experts, eux, peuvent dénicher exactement ce que vous cherchez. Dites-nous ce que vous avez en tête.') +
        '</div>';
      const noResultRestartBtn = container.querySelector('.sim-no-result-restart');
      if (noResultRestartBtn) {
        noResultRestartBtn.addEventListener('click', function() {
          if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
          window.scrollTo(0, 0);
          location.reload();
        });
      }
      document.getElementById('simulateur-results-wrapper').classList.add('is-visible');
      return;
    }

    trackEvent('sim_finished', {
      bottle_count: totalBottles,
      wine_count: wineCount,
      total_price: Math.round(totalPrice),
    });

    const statsContainer = document.getElementById('sim-results-stats');
    if (statsContainer) {
      statsContainer.innerHTML = '<div class="sim-stat-card"><span class="sim-stat-icon sim-stat-icon-bottles" aria-hidden="true">🍾</span><strong>' + totalBottles + '</strong><span>Bouteilles au total</span></div>' +
        '<div class="sim-stat-card"><span class="sim-stat-icon sim-stat-icon-domain" aria-hidden="true">⌂</span><strong>' + wineCount + '</strong><span>Vins différents</span></div>' +
        '<div class="sim-stat-card"><span class="sim-stat-icon sim-stat-icon-price" aria-hidden="true">●●●</span><strong>' + fmt(totalPrice) + '</strong><span>Estimés</span></div>';
    }

    renderCartInsert(products);

    HORIZONS.forEach(function(item) {
      const hKey = item.key;
      const hGroup = products.filter(function(p) { return p.horizon === hKey; });
      if (!hGroup.length) return;

      const groupBottles = hGroup.reduce(function(sum, p) { return sum + p.packSize; }, 0);
      const groupWines = new Set(hGroup.map(function(p) { return p.handle || p.id; }).filter(Boolean)).size;
      const sec = document.createElement('div');
      sec.className = 'sim-horizon-section';
      sec.style.marginBottom = '40px';

      const gridId = 'grid-' + hKey;
      const footerId = 'footer-' + hKey;
      const secHtml = '<h2 class="sim-horizon-title"><span>' + item.label + '</span><small>' + groupBottles + ' bouteilles <b>|</b> ' + groupWines + ' vins différents</small></h2>' +
        '<div class="sim-products-grid" id="' + gridId + '"></div>' +
        '<div class="sim-horizon-footer" id="' + footerId + '"></div>';

      sec.innerHTML = secHtml;
      container.appendChild(sec);
      renderCards(hGroup, gridId, footerId);
    });

    document.getElementById('simulateur-results-wrapper').classList.add('is-visible');
    const wrapper = document.getElementById('sim-wrapper');
    if (wrapper) wrapper.classList.add('sim-has-results');
    // renderCartInsert() a tourné plus haut avant que .is-visible ne soit posé (le wrapper
    // était donc encore display:none, geometry non fiable) — on relance maintenant qu'il est
    // réellement affiché. syncResultsBottomPadding() n'est appelée qu'ici, une seule fois pour
    // toute la durée de vie de la page (voir sa doc) — ne pas ajouter d'autres appels ailleurs.
    syncCartBarLayout();
    syncResultsBottomPadding();
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Encart "contactez-nous" partagé entre l'écran "aucun résultat" et l'écran d'erreur
  // technique — même bouton/lien, seul le message d'accroche et la classe wrapper diffèrent.
  function renderContactCta(wrapClass, message) {
    return '<div class="' + wrapClass + '">' +
      '<p>' + message + '</p>' +
      '<a class="sim-btn sim-btn-primary" href="https://www.cavissima.com/pages/contact" target="_blank" rel="noopener">Contactez-nous</a>' +
      '</div>';
  }

  function renderCards(products, gridId, footerId) {
    const grid = document.getElementById(gridId);
    const footer = document.getElementById(footerId);
    if (!grid) return;
    grid.innerHTML = '';
    if (footer) footer.innerHTML = '';

    const PREVIEW_LIMIT = 4;

    products.forEach(function(p, idx) {
      const card = document.createElement('div');
      const isHidden = idx >= PREVIEW_LIMIT;
      const isLastPreview = products.length > PREVIEW_LIMIT && idx === PREVIEW_LIMIT - 1;
      const isCollection = isCollectionProduct(p);
      const isLockedCollection = isCollection && !customerHasCollectionAccess();
      card.className = 'sim-product-card' + (isHidden ? ' sim-card-hidden' : '') + (isLastPreview ? ' sim-card-preview-last' : '') + (isCollection ? ' sim-card-collection' : '');

      let imgHtml = p.image
        ? '<img src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title) + '" loading="lazy">'
        : '<div class="sim-card-no-image"></div>';

      const hasDiscount = p.compareAtPrice && p.compareAtPrice > p.price;
      const priceHtml = hasDiscount
        ? '<span class="sim-card-price-was">' + fmt(p.compareAtPrice) + '</span><span class="sim-card-price-now">' + fmt(p.price) + ' TTC</span>'
        : fmt(p.price) + ' TTC';

      card.innerHTML = '<label class="sim-cart-check-wrap' + (isLockedCollection ? ' sim-cart-check-collection' : '') + '">' +
        '<input type="checkbox" class="sim-cart-check" data-product-id="' + escapeHtml(p.id) + '" data-pack-size="' + escapeHtml(p.packSize) + '"' + (isLockedCollection ? ' disabled' : ' checked') + '>' +
        '</label>' +
        '<div class="sim-card-image">' + imgHtml + '</div>' +
        '<div class="sim-card-body">' +
        '  <div class="sim-card-title-wrap">' +
        (isCollection ? '<span class="sim-card-collection-badge">Exclusivité Collection</span>' : '') +
        '  <h3 class="sim-card-title">' + escapeHtml(p.title) + '</h3>' +
        '  </div>' +
        '  <span class="sim-card-color">' + escapeHtml(formatDisplayValue(p.color || 'Vins')) + '</span>' +
        '  <span class="sim-card-region">' + escapeHtml(p.region ? formatDisplayValue(p.region) : '-') + '</span>' +
        '  <span class="sim-card-vintage-value">' + escapeHtml(p.vintage || '-') + '</span>' +
        '  <span class="sim-card-qty">' + escapeHtml(p.variantTitle || '-') + '</span>' +
        '  <span class="sim-card-price">' + priceHtml + '</span>' +
        '  <a href="https://' + CONFIG.shopDomain + '/products/' + encodeURIComponent(p.handle) + '" class="sim-card-cta" target="_blank" rel="noopener">Voir</a>' +
        '</div>';

      grid.appendChild(card);
    });

    // Sans ça, la marge réservée pour le dégradé + bouton "Voir les X autres références"
    // (padding-bottom sur la grille, remonté par le margin-top négatif de .sim-horizon-footer)
    // restait appliquée même quand ce bouton n'existe pas (≤ PREVIEW_LIMIT produits), laissant
    // un vide sous chaque horizon complet.
    grid.classList.toggle('sim-products-grid-has-expand', products.length > PREVIEW_LIMIT);

    if (products.length > PREVIEW_LIMIT && footer) {
      const hiddenCount = products.length - PREVIEW_LIMIT;
      const btn = document.createElement('button');
      btn.className = 'sim-btn-expand';
      btn.textContent = 'Voir les ' + hiddenCount + ' autre' + (hiddenCount > 1 ? 's' : '') + ' référence' + (hiddenCount > 1 ? 's' : '');

      let expanded = false;
      btn.addEventListener('click', function() {
        expanded = !expanded;
        grid.classList.toggle('is-expanded', expanded);
        const cards = grid.querySelectorAll('.sim-product-card');
        cards.forEach(function(card, idx) {
          if (idx >= PREVIEW_LIMIT) {
            card.classList.toggle('sim-card-hidden', !expanded);
          }
        });
        btn.textContent = expanded
          ? 'Voir moins'
          : 'Voir les ' + hiddenCount + ' autre' + (hiddenCount > 1 ? 's' : '') + ' référence' + (hiddenCount > 1 ? 's' : '');

        if (!expanded) {
          const section = grid.closest('.sim-horizon-section');
          if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });

      footer.appendChild(btn);
    }
  }

  function formatDisplayValue(value) {
    return String(value)
      .split(/\s+/)
      .map(function(word) { return word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : ''; })
      .join(' ');
  }

  function fmt(price) {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);
  }

  // ---- EXPORT PDF (jsPDF + autoTable, chargés à la demande) ----
  // Hash SRI calculé sur le fichier réellement servi (pin de version) : si un CDN sert un
  // contenu différent, le navigateur bloque le script (onerror) et le fallback suivant est tenté.
  const JSPDF_SRI = 'sha384-en/ztfPSRkGfME4KIm05joYXynqzUgbsG5nMrj/xEFAHXkeZfO3yMK8QQ+mP7p1/';
  const AUTOTABLE_SRI = 'sha384-fCAW/rDWORTbQXSiB7mOg0QtQ5c+r0f544y6XoKjuVva0nMBlCpNUjiFeG5iMdS3';

  const JSPDF_URLS = [
    'https://cdn.jsdelivr.net/npm/jspdf@2.5.2/dist/jspdf.umd.min.js',
    'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.2/jspdf.umd.min.js',
    'https://unpkg.com/jspdf@2.5.2/dist/jspdf.umd.min.js'
  ];
  const AUTOTABLE_URLS = [
    'https://cdn.jsdelivr.net/npm/jspdf-autotable@3.8.2/dist/jspdf.plugin.autotable.min.js',
    'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js',
    'https://unpkg.com/jspdf-autotable@3.8.2/dist/jspdf.plugin.autotable.min.js'
  ];

  // Logo Cavissima et photo SAV hébergés sur le CDN Shopify (Fichiers) plutôt qu'embarqués en
  // base64 dans ce script : les deux ne servent qu'à la génération du PDF, inutile de les faire
  // télécharger/parser par tous les visiteurs à chaque chargement de la page. Récupérés en
  // base64 à la volée (fetchImageAsDataURL) uniquement au moment du clic "Télécharger le PDF".
  const CAVISSIMA_LOGO_URL = 'https://cdn.shopify.com/s/files/1/0906/7629/8075/files/cavissima-logo-noir.png?v=1789474135';
  const SAV_PHOTO_URL = 'https://cdn.shopify.com/s/files/1/0906/7629/8075/files/julie_sav.png?v=1789474457';

  function fetchImageAsDataURL(url) {
    return fetch(url)
      .then(function(res) {
        if (!res.ok) throw new Error('Image HTTP ' + res.status + ' : ' + url);
        return res.blob();
      })
      .then(function(blob) {
        return new Promise(function(resolve, reject) {
          const reader = new FileReader();
          reader.onload = function() { resolve(reader.result); };
          reader.onerror = function() { reject(new Error('Lecture image echouee : ' + url)); };
          reader.readAsDataURL(blob);
        });
      });
  }

  function loadScriptOnce(src, sri) {
    return new Promise(function(resolve, reject) {
      if (document.querySelector('script[src="' + src + '"]')) { resolve(); return; }
      const s = document.createElement('script');
      s.src = src;
      if (sri) {
        s.integrity = sri;
        s.crossOrigin = 'anonymous';
      }
      s.onload = function() { resolve(); };
      s.onerror = function() { reject(new Error('Échec du chargement de ' + src)); };
      document.head.appendChild(s);
    });
  }

  function loadScriptWithFallback(urls, sri) {
    let chain = Promise.reject();
    urls.forEach(function(url) {
      chain = chain.catch(function() { return loadScriptOnce(url, sri); });
    });
    return chain;
  }

  function loadPdfLibs() {
    const ready = window.jspdf && window.jspdf.jsPDF && window.jspdf.jsPDF.API && window.jspdf.jsPDF.API.autoTable;
    if (ready) return Promise.resolve();
    return loadScriptWithFallback(JSPDF_URLS, JSPDF_SRI).then(function() { return loadScriptWithFallback(AUTOTABLE_URLS, AUTOTABLE_SRI); });
  }

  function stripEmoji(str) {
    return String(str || '')
      .replace(/[\u{1F000}-\u{1FFFF}\u{2190}-\u{2BFF}\u{FE0F}\u{2600}-\u{27BF}]/gu, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function fmtPdf(price) {
    return fmt(price).replace(/[    ]/g, ' ');
  }

  async function generatePdfReport() {
    const cellar = state.idealCellar || [];
    if (!cellar.length) {
      alert("Aucune sélection à exporter. Terminez d'abord la simulation.");
      return;
    }

    // Chargées en parallèle du reste : jsPDF/autoTable via CDN, logo + photo SAV via le CDN
    // Shopify (voir fetchImageAsDataURL plus haut) — aucune des deux n'est embarquée dans ce
    // script, elles ne sont récupérées qu'au moment où l'utilisateur demande vraiment le PDF.
    const [, logoDataUrl, savPhotoDataUrl] = await Promise.all([
      loadPdfLibs(),
      fetchImageAsDataURL(CAVISSIMA_LOGO_URL),
      fetchImageAsDataURL(SAV_PHOTO_URL),
    ]);

    const jsPDF = window.jspdf.jsPDF;
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 14;
    const contentWidth = pageWidth - marginX * 2;

    const NAVY = [8, 15, 24];
    const BLACK = [10, 10, 10];
    const DARK_TEXT = [26, 26, 26];
    const MUTED_GRAY = [102, 102, 102];
    const BORDER_GRAY = [234, 234, 234];
    const RED_PRICE = [156, 0, 31];

    // Géométrie du badge "Exclusivité Collection" dans le tableau des vins — partagée entre
    // didParseCell (calcul de la hauteur de ligne réservée) et didDrawCell (dessin réel), pour
    // que la hauteur réservée corresponde toujours exactement à ce qui est dessiné, quel que
    // soit le nombre de lignes du titre (un titre long ne doit plus déborder sur le pied de page).
    //
    // columnWidth/columnPadding sont des valeurs FIXES (pas data.cell.width/.padding()) : au
    // moment de didParseCell, jsPDF-autotable n'a pas encore figé la largeur finale des colonnes
    // pour la table entière, donc data.cell.width y renvoie une estimation bien plus étroite que
    // les 60mm réels de columnStyles[0].cellWidth ci-dessous — ça faisait calculer beaucoup trop
    // de lignes de titre (et donc une hauteur de cellule démesurée) alors que le texte, dessiné
    // plus tard dans didDrawCell une fois la largeur réelle connue, ne prenait que 2-3 lignes.
    // En figeant la même largeur de calcul dans les deux hooks, la hauteur réservée correspond
    // toujours exactement au nombre de lignes réellement dessinées.
    const COLLECTION_BADGE = {
      fontSize: 5.5,
      padX: 2,
      height: 3.2,
      topMargin: 2.2,
      gapToTitle: 2.4,
      titleFontSize: 8.5,
      titleLineHeight: 3.3,
      bottomMargin: 2,
      columnWidth: 60, // doit rester synchronisé avec columnStyles: { 0: { cellWidth: 60 } } plus bas
      columnPadding: 6, // estimation prudente (plutôt trop que pas assez) du padding gauche+droite
    };

    const totalPrice = cellar.reduce(function(sum, p) { return sum + p.price; }, 0);
    const totalBottles = cellar.reduce(function(sum, p) { return sum + p.packSize; }, 0);
    const wineCount = new Set(cellar.map(function(p) { return p.handle || p.id; }).filter(Boolean)).size;

    doc.setProperties({ title: 'Cavissima – Votre cave idéale (' + totalBottles + ' bouteilles)' });

    function drawStatCard(x, cardY, w, h, value, label) {
      doc.setDrawColor(BORDER_GRAY[0], BORDER_GRAY[1], BORDER_GRAY[2]);
      doc.setLineWidth(0.3);
      doc.rect(x, cardY, w, h, 'S');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.setTextColor(BLACK[0], BLACK[1], BLACK[2]);
      doc.text(value, x + w / 2, cardY + h / 2, { align: 'center' });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(DARK_TEXT[0], DARK_TEXT[1], DARK_TEXT[2]);
      doc.text(label, x + w / 2, cardY + h / 2 + 8, { align: 'center' });
    }

    let y = 20;

    const logoWidth = 44;
    const logoHeight = logoWidth * (454 / 2480);
    doc.addImage(logoDataUrl, 'PNG', pageWidth / 2 - logoWidth / 2, y, logoWidth, logoHeight);
    y += logoHeight + 10;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(24);
    doc.setTextColor(BLACK[0], BLACK[1], BLACK[2]);
    doc.text('VOTRE CAVE IDÉALE', pageWidth / 2, y, { align: 'center' });
    y += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(MUTED_GRAY[0], MUTED_GRAY[1], MUTED_GRAY[2]);
    const todayStr = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }).format(new Date());
    doc.text('Simulation générée le ' + todayStr, pageWidth / 2, y, { align: 'center' });
    y += 14;

    const statGap = 6;
    const statCardWidth = (contentWidth - statGap * 2) / 3;
    const statCardHeight = 26;
    drawStatCard(marginX, y, statCardWidth, statCardHeight, String(totalBottles), 'Bouteilles au total');
    drawStatCard(marginX + statCardWidth + statGap, y, statCardWidth, statCardHeight, String(wineCount), 'Vins différents');
    drawStatCard(marginX + (statCardWidth + statGap) * 2, y, statCardWidth, statCardHeight, fmtPdf(totalPrice), 'Budget estimé');
    y += statCardHeight + 12;

    const allRecapItems = getRecapItems();
    const recapItems = allRecapItems.filter(function(item) { return item.val && item.val !== '-'; });
    if (recapItems.length) {
      const recapBoxTop = y;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(BLACK[0], BLACK[1], BLACK[2]);
      doc.text('Récapitulatif de votre simulation', marginX + 4, y + 10);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(MUTED_GRAY[0], MUTED_GRAY[1], MUTED_GRAY[2]);
      doc.text(recapItems.length + ' / ' + allRecapItems.length + ' critères remplis', pageWidth - marginX - 4, y + 10, { align: 'right' });

      doc.autoTable({
        startY: y + 16,
        margin: { left: marginX + 4, right: marginX + 4 },
        tableWidth: contentWidth - 8,
        head: [['Critère', 'Votre choix']],
        body: recapItems.map(function(item) { return [stripEmoji(item.label), stripEmoji(item.val)]; }),
        theme: 'plain',
        headStyles: { textColor: MUTED_GRAY, fontStyle: 'bold', fontSize: 8 },
        styles: { fontSize: 9.5, textColor: DARK_TEXT, lineColor: BORDER_GRAY, lineWidth: { bottom: 0.2 } },
      });

      const recapBoxHeight = doc.lastAutoTable.finalY - recapBoxTop + 8;
      doc.setDrawColor(BORDER_GRAY[0], BORDER_GRAY[1], BORDER_GRAY[2]);
      doc.setLineWidth(0.3);
      doc.rect(marginX, recapBoxTop, contentWidth, recapBoxHeight, 'S');

      y = recapBoxTop + recapBoxHeight + 12;
    }

    HORIZONS.forEach(function(h) {
      const group = cellar.filter(function(p) { return p.horizon === h.key; });
      if (!group.length) return;

      if (y > pageHeight - 40) {
        doc.addPage();
        y = 20;
      }

      const groupBottles = group.reduce(function(sum, p) { return sum + p.packSize; }, 0);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(BLACK[0], BLACK[1], BLACK[2]);
      doc.text(stripEmoji(h.label), marginX, y);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(MUTED_GRAY[0], MUTED_GRAY[1], MUTED_GRAY[2]);
      doc.text(groupBottles + ' bouteilles  |  ' + group.length + ' vins différents', pageWidth - marginX, y, { align: 'right' });
      y += 2;
      doc.setDrawColor(BLACK[0], BLACK[1], BLACK[2]);
      doc.setLineWidth(0.4);
      doc.line(marginX, y, pageWidth - marginX, y);
      y += 6;

      doc.autoTable({
        startY: y,
        margin: { left: marginX, right: marginX },
        head: [['Vin', 'Couleur', 'Région', 'Millésime', 'Format', 'Prix TTC']],
        body: group.map(function(p) {
          return [
            p.title || '-',
            formatDisplayValue(p.color || '-'),
            p.region ? formatDisplayValue(p.region) : '-',
            p.vintage || '-',
            p.variantTitle || '-',
            fmtPdf(p.price)
          ];
        }),
        theme: 'grid',
        headStyles: { fillColor: BLACK, textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
        styles: { fontSize: 8.5, textColor: DARK_TEXT, lineColor: BORDER_GRAY, lineWidth: 0.15 },
        columnStyles: { 0: { cellWidth: 60 } },
        didParseCell: function(data) {
          if (data.section === 'body' && data.column.index === 0) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.textColor = DARK_TEXT;
            const p = group[data.row.index];
            if (p && isCollectionProduct(p)) {
              // Même logique que la colonne prix ci-dessous : on bloque le texte auto pour
              // tout dessiner nous-mêmes dans didDrawCell (badge "Exclusivité Collection"
              // identique à celui de la page de résultats, en bordeaux #6d1a2b). La hauteur
              // réservée est calculée à partir du nombre réel de lignes du titre — une valeur
              // fixe débordait sur les titres longs (2-3 lignes), jusque sur le pied de page.
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(COLLECTION_BADGE.titleFontSize);
              const cb = COLLECTION_BADGE;
              const titleLines = doc.splitTextToSize(p.title || '-', cb.columnWidth - cb.columnPadding);
              data.cell.styles.minCellHeight = cb.topMargin + cb.height + cb.gapToTitle
                + titleLines.length * cb.titleLineHeight + cb.bottomMargin;
              data.cell.text = [''];
            }
          }
          if (data.section === 'body' && data.column.index === 5) {
            const p = group[data.row.index];
            if (p && p.compareAtPrice && p.compareAtPrice > p.price) {
              // Réserve la hauteur pour 2 lignes et bloque le texte auto : on dessine tout nous-mêmes
              // dans didDrawCell pour éviter que jsPDF-autotable superpose son propre rendu par-dessus.
              data.cell.styles.minCellHeight = 12;
              data.cell.text = [''];
            }
          }
        },
        didDrawCell: function(data) {
          if (data.section === 'body' && data.column.index === 0) {
            const p = group[data.row.index];
            if (p && isCollectionProduct(p)) {
              const cb = COLLECTION_BADGE;
              const textX = data.cell.x + data.cell.padding('left');
              const badgeText = 'EXCLUSIVITÉ COLLECTION';
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(cb.fontSize);
              const badgeTextWidth = doc.getTextWidth(badgeText);
              const badgeY = data.cell.y + cb.topMargin;
              doc.setFillColor(109, 26, 43);
              doc.rect(textX, badgeY, badgeTextWidth + cb.padX * 2, cb.height, 'F');
              doc.setTextColor(255, 255, 255);
              doc.text(badgeText, textX + cb.padX, badgeY + cb.height - 1.0);

              doc.setFont('helvetica', 'bold');
              doc.setFontSize(cb.titleFontSize);
              doc.setTextColor(DARK_TEXT[0], DARK_TEXT[1], DARK_TEXT[2]);
              const titleLines = doc.splitTextToSize(p.title || '-', cb.columnWidth - cb.columnPadding);
              doc.text(titleLines, textX, badgeY + cb.height + cb.gapToTitle + cb.titleLineHeight - 1.2);
            }
            if (p && p.handle) {
              const url = 'https://' + CONFIG.shopDomain + '/products/' + p.handle;
              doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url: url });
            }
          }
          if (data.section === 'body' && data.column.index === 5) {
            const p = group[data.row.index];
            if (p && p.compareAtPrice && p.compareAtPrice > p.price) {
              const compareText = fmtPdf(p.compareAtPrice);
              const priceText = fmtPdf(p.price);
              const textX = data.cell.x + data.cell.padding('left');
              const midY = data.cell.y + data.cell.height / 2;

              doc.setFont('helvetica', 'normal');
              doc.setFontSize(7.2);
              doc.setTextColor(MUTED_GRAY[0], MUTED_GRAY[1], MUTED_GRAY[2]);
              const line1Y = midY - 1.6;
              doc.text(compareText, textX, line1Y);
              const textWidth = doc.getTextWidth(compareText);
              doc.setDrawColor(MUTED_GRAY[0], MUTED_GRAY[1], MUTED_GRAY[2]);
              doc.setLineWidth(0.25);
              doc.line(textX, line1Y - 1.1, textX + textWidth, line1Y - 1.1);

              doc.setFont('helvetica', 'bold');
              doc.setFontSize(8.5);
              doc.setTextColor(RED_PRICE[0], RED_PRICE[1], RED_PRICE[2]);
              doc.text(priceText, textX, midY + 3.6);
            }
          }
        },
      });
      y = doc.lastAutoTable.finalY + 12;
    });

    // ---- ENCART CONTACT ----
    const ctaPaddingX = 12;
    const ctaPaddingY = 10;
    const ctaTitle = 'Un projet de cave spécifique ou un conseil dédié ?';
    const ctaBody = "Que vous ayez un projet de cave à vin spécifique en tête ou que vous ayez besoin de conseils dédiés, les experts de Cavissima à Bordeaux sont à votre écoute pour vous accompagner dans la sélection des vins et l'aménagement de votre espace de conservation.";

    // Photo d'une conseillère Cavissima à gauche du texte — rend l'encart moins austère qu'un
    // simple pavé de texte. Largeur fixe, hauteur dérivée du ratio réel du fichier source
    // (1207x1185px, quasi carré) pour ne pas déformer la photo.
    const ctaPhotoWidth = 30;
    const ctaPhotoHeight = ctaPhotoWidth * (1185 / 1207);
    const ctaGap = 8;
    const ctaTextX = marginX + ctaPaddingX + ctaPhotoWidth + ctaGap;
    const ctaTextWidth = (marginX + contentWidth - ctaPaddingX) - ctaTextX;
    const ctaTextCenterX = ctaTextX + ctaTextWidth / 2;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    const ctaTitleLines = doc.splitTextToSize(ctaTitle, ctaTextWidth);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const ctaBodyLines = doc.splitTextToSize(ctaBody, ctaTextWidth);

    const ctaContactText = 'www.cavissima.com/pages/contact   ·   +33 (0)7 88 21 05 63';
    const ctaContactUrl = 'https://www.cavissima.com/pages/contact';

    const ctaTitleLineHeight = 6;
    const ctaBodyLineHeight = 5;
    const ctaContactLineHeight = 6;
    // Écart entre le bloc photo+texte et la ligne de contact — plus généreux qu'un simple
    // saut de ligne pour bien détacher visuellement le contact du reste (signalé trop collé).
    const ctaContactGap = 14;
    // Bloc photo+texte : les deux sont côte à côte, la hauteur du bloc est donc le plus grand
    // des deux (pas leur somme) — la légende contact vient ensuite en dessous, sur toute la largeur.
    const ctaTextBlockHeight = (ctaTitleLines.length * ctaTitleLineHeight) + 3 + (ctaBodyLines.length * ctaBodyLineHeight);
    const ctaTopBlockHeight = Math.max(ctaPhotoHeight, ctaTextBlockHeight);
    const ctaHeight = ctaPaddingY * 2 + ctaTopBlockHeight + ctaContactGap + ctaContactLineHeight;

    if (y + ctaHeight > pageHeight - 15) {
      doc.addPage();
      y = 20;
    }

    doc.setFillColor(NAVY[0], NAVY[1], NAVY[2]);
    doc.rect(marginX, y, contentWidth, ctaHeight, 'F');

    const ctaTopBlockTop = y + ctaPaddingY;
    const ctaPhotoY = ctaTopBlockTop + (ctaTopBlockHeight - ctaPhotoHeight) / 2;
    doc.addImage(savPhotoDataUrl, 'PNG', marginX + ctaPaddingX, ctaPhotoY, ctaPhotoWidth, ctaPhotoHeight);
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.4);
    doc.rect(marginX + ctaPaddingX, ctaPhotoY, ctaPhotoWidth, ctaPhotoHeight, 'S');

    let ctaY = ctaTopBlockTop + (ctaTopBlockHeight - ctaTextBlockHeight) / 2 + 4;
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    ctaTitleLines.forEach(function(line) {
      doc.text(line, ctaTextCenterX, ctaY, { align: 'center' });
      ctaY += ctaTitleLineHeight;
    });
    ctaY += 3;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(215, 215, 215);
    ctaBodyLines.forEach(function(line) {
      doc.text(line, ctaTextCenterX, ctaY, { align: 'center' });
      ctaY += ctaBodyLineHeight;
    });

    const ctaContactY = ctaTopBlockTop + ctaTopBlockHeight + ctaContactGap;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(255, 255, 255);
    doc.text(ctaContactText, pageWidth / 2, ctaContactY, { align: 'center' });
    const ctaContactTextWidth = doc.getTextWidth(ctaContactText);
    doc.link(pageWidth / 2 - ctaContactTextWidth / 2, ctaContactY - ctaContactLineHeight + 2, ctaContactTextWidth, ctaContactLineHeight, { url: ctaContactUrl });

    y += ctaHeight + 10;

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(7.5);
      doc.setTextColor(150, 150, 150);
      doc.text('Cavissima — Suggestion générée à titre indicatif : nos experts se tiennent prêts à l\'affiner avec vous.', marginX, pageHeight - 8);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('Page ' + i + ' / ' + pageCount, pageWidth - marginX, pageHeight - 8, { align: 'right' });
    }

    doc.save('cavissima-cave-ideale-' + totalBottles + '-bouteilles.pdf');
    trackEvent('sim_pdf_download', { bottle_count: totalBottles });
  }

  // ---- NAVIGATION DU QUIZ ET ÉVÉNEMENTS ----
  const STEPS = ['projet','situation','capacite','budget','budgetMax','couleurs','regions','quantite','formats'];
  const STEP_SUBTITLES = {
    projet: 'Construisons ensemble une cave qui vous ressemble.',
    situation: 'Cela nous permet d\'adapter nos recommandations à votre cave actuelle.',
    capacite: 'Cette estimation nous aidera à équilibrer votre sélection.',
    budget: 'Définissez le budget moyen souhaité par bouteille.',
    budgetMax: 'Indiquez le montant maximum que vous ne souhaitez pas dépasser — c\'est facultatif.',
    couleurs: 'Sélectionnez une ou plusieurs couleurs de vins.',
    regions: 'Sélectionnez les régions viticoles que vous souhaitez retrouver dans votre cave.',
    quantite: 'Sélectionnez un ou plusieurs conditionnements acceptés.',
    formats: 'Sélectionnez le ou les formats de bouteille que vous souhaitez retrouver dans votre sélection.'
  };

  // Remonte le haut du simulateur en haut de l'écran — sans ça, changer d'étape ou afficher
  // les résultats laissait la page au scroll où elle était (ex: question longue lue jusqu'en
  // bas, "Valider" cliqué depuis là), et la nouvelle étape/les résultats s'affichaient hors
  // champ au lieu de repartir du haut.
  //
  // SCROLL_TOP_EXTRA : marge remontée en plus du simple haut du wrapper — un scrollIntoView
  // strict s'arrête pile au bord du wrapper, ce qui laissait encore trop de contenu de la
  // question précédente visible en haut d'écran (signalé par le client à la fin du quiz).
  const SCROLL_TOP_EXTRA = 40;

  function scrollSimToTop() {
    const wrapper = document.getElementById('sim-wrapper');
    if (!wrapper) return;
    const targetY = window.scrollY + wrapper.getBoundingClientRect().top - SCROLL_TOP_EXTRA;
    window.scrollTo({ top: Math.max(targetY, 0), behavior: 'smooth' });
  }

  function showStep(i) {
    const steps = document.querySelectorAll('.sim-step');
    steps.forEach(function(el, idx) { el.classList.toggle('is-active', idx === i); });
    state.currentStep = i;
    const bar = document.getElementById('sim-progress-bar');
    const progress = (((i + 1) / STEPS.length) * 100) + '%';
    if (bar) bar.style.setProperty('width', progress, 'important');
    const progressTrack = document.getElementById('sim-progress');
    if (progressTrack) progressTrack.style.setProperty('--sim-progress', progress);
    const ctr = document.getElementById('sim-progress-counter');
    if (ctr) ctr.textContent = (i + 1) + ' / ' + STEPS.length;
    const step = steps[i];
    if (step) {
      const subtitleText = STEP_SUBTITLES[STEPS[i]];
      let subtitle = step.querySelector('.sim-step-subtitle');
      if (subtitleText) {
        if (!subtitle) {
          subtitle = document.createElement('p');
          subtitle.className = 'sim-step-subtitle';
          step.querySelector('h2').insertAdjacentElement('afterend', subtitle);
        }
        subtitle.textContent = subtitleText;
      } else if (subtitle) {
        subtitle.remove();
      }
      updateStepControls(step);
    }
  }

  function updateStepControls(step) {
    const nextButton = step ? step.querySelector('[data-action="next"], [data-action="finish"]') : null;
    if (!nextButton) return;
    const field = step.dataset.field;
    let answered = false;
    if (field === 'budgetMax') {
      answered = Boolean(state.answers.budgetMax) || Boolean(step.querySelector('#sim-btn-no-limit.is-selected'));
    } else if (Array.isArray(state.answers[field])) {
      answered = state.answers[field].length > 0;
    } else {
      answered = Boolean(state.answers[field]);
    }
    nextButton.disabled = !answered;
  }

  function next() {
    if (state.currentStep === 0) trackEvent('sim_start', {});
    trackEvent('sim_step_completed', { step_index: state.currentStep + 1, step_name: STEPS[state.currentStep] });
    if (state.currentStep < STEPS.length - 1) { showStep(state.currentStep + 1); scrollSimToTop(); }
    else finish();
  }
  function prev() {
    if (state.currentStep > 0) { showStep(state.currentStep - 1); scrollSimToTop(); }
  }

  function finish() {
    document.getElementById('simulateur-quiz').classList.add('is-done');
    const bar = document.getElementById('sim-progress-bar');
    if (bar) bar.style.setProperty('width', '100%', 'important');
    const progressTrack = document.getElementById('sim-progress');
    if (progressTrack) progressTrack.style.setProperty('--sim-progress', '100%');
    document.getElementById('simulateur-loading').classList.add('is-visible');
    scrollSimToTop();
    run();
  }

  // ---- KLAVIYO : MAJ PROFIL (prénom/nom + éventuellement consent_tracking) ----
  function updateKlaviyoProfile(email, firstName, lastName, wantsPixel) {
    const attributes = {
      email: email,
      first_name: firstName,
      last_name: lastName
    };

    if (wantsPixel) {
      attributes.properties = { consent_tracking: true };
    }

    return fetch('https://a.klaviyo.com/client/profiles/?company_id=' + KLAVIYO_PUBLIC_KEY, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2024-10-15'
      },
      body: JSON.stringify({
        data: {
          type: 'profile',
          attributes: attributes
        }
      })
    });
  }

  // ---- KLAVIYO : ABONNEMENT MARKETING (case "optin") ----
  const KLAVIYO_LIST_ID = 'V3HN4i';

  function subscribeKlaviyoProfile(email, firstName, lastName) {
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                first_name: firstName,
                last_name: lastName,
                subscriptions: {
                  email: { marketing: { consent: 'SUBSCRIBED' } }
                }
              }
            }
          },
          custom_source: 'Simulateur cave idéale'
        }
      }
    };

    if (KLAVIYO_LIST_ID) {
      payload.data.relationships = {
        list: { data: { type: 'list', id: KLAVIYO_LIST_ID } }
      };
    }

    return fetch('https://a.klaviyo.com/client/subscriptions?company_id=' + KLAVIYO_PUBLIC_KEY, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2025-01-15'
      },
      body: JSON.stringify(payload)
    });
  }

  // ---- KLAVIYO : NOTIFICATIONS INTERNES (no-result, avis) — envoyées vers MONITORING_EMAIL ----
  function sendMonitoringEvent(metricName, extraProperties) {
    const recapItems = getRecapItems();
    const recapText = recapItems.map(function(item) { return item.label + ': ' + item.val; }).join(' | ');
    const debugEl = document.getElementById('sim-debug');
    const debugText = debugEl ? debugEl.textContent : '';

    const properties = Object.assign({
      Recap: recapText,
      Recap_Items: getRecapItemsForKlaviyo(),
      Detail_Technique: debugText,
      Page_URL: window.location.href,
    }, extraProperties || {});

    return fetch('https://a.klaviyo.com/client/events/?company_id=' + KLAVIYO_PUBLIC_KEY, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2024-10-15'
      },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            metric: {
              data: {
                type: 'metric',
                attributes: { name: metricName }
              }
            },
            profile: {
              data: {
                type: 'profile',
                attributes: { email: MONITORING_EMAIL }
              }
            },
            properties: properties
          }
        }
      })
    });
  }

  // Popin de sauvegarde réutilisée pour deux intentions distinctes : télécharger le PDF
  // (envoie l'event Cave_Ideale_Simulee au profil du client) ou se faire contacter (envoie
  // en plus Demande_Contact_Expert au profil interne, avec le détail complet de la sélection).
  const MODAL_COPY = {
    pdf: {
      title: 'Téléchargez votre cave idéale en PDF',
      intro: 'Indiquez votre e-mail pour recevoir votre simulation et télécharger votre PDF.',
      submitIdle: 'Télécharger mon PDF',
      submitPending: 'Génération du PDF…',
      successMsg: 'Votre PDF a été téléchargé et envoyé par e-mail !',
    },
    contact: {
      title: 'Échangez avec un expert Cavissima',
      intro: 'Laissez-nous vos coordonnées, un expert Cavissima vous recontacte pour affiner votre sélection et répondre à vos questions.',
      submitIdle: 'Nous contacter',
      submitPending: 'Envoi en cours…',
      successMsg: 'Votre demande a bien été transmise, un expert vous recontacte sous peu !',
    },
  };

  function bindEvents() {
    const maxBudgetInput = document.getElementById('sim-input-budget-max');
    const noLimitBtn = document.getElementById('sim-btn-no-limit');

    if (maxBudgetInput) {
      maxBudgetInput.addEventListener('input', function() {
        const val = parseFloat(maxBudgetInput.value);
        state.answers.budgetMax = (val && val > 0) ? val : null;
        if (noLimitBtn) noLimitBtn.classList.remove('is-selected');
        updateStepControls(maxBudgetInput.closest('.sim-step'));
      });
    }

    if (noLimitBtn) {
      noLimitBtn.addEventListener('click', function() {
        state.answers.budgetMax = null;
        if (maxBudgetInput) maxBudgetInput.value = '';
        noLimitBtn.classList.add('is-selected');
        updateStepControls(noLimitBtn.closest('.sim-step'));
      });
    }

    // ENVOI EMAIL KLAVIYO
    const btnSaveEmail = document.getElementById('sim-btn-save-email');
    if (btnSaveEmail) {
      btnSaveEmail.addEventListener('click', function(event) {
        event.preventDefault();
        const modalForm = document.getElementById('sim-save-form-modal');
        if (modalForm && !modalForm.checkValidity()) {
          modalForm.reportValidity();
          return;
        }
        const copy = MODAL_COPY[simModalIntent];
        const inputEmailEl = document.getElementById('sim-save-email');
        const inputEmail = inputEmailEl ? inputEmailEl.value.trim() : '';
        const inputFirstNameEl = document.getElementById('sim-save-firstname');
        const inputFirstName = inputFirstNameEl ? inputFirstNameEl.value.trim() : '';
        const inputLastNameEl = document.getElementById('sim-save-lastname');
        const inputLastName = inputLastNameEl ? inputLastNameEl.value.trim() : '';
        const inputPhoneEl = document.getElementById('sim-save-phone');
        const inputPhone = inputPhoneEl ? inputPhoneEl.value.trim() : '';
        const inputCommentEl = document.getElementById('sim-save-comment');
        const inputComment = inputCommentEl ? inputCommentEl.value.trim() : '';

        if (!inputEmail || !inputEmail.includes('@')) {
          if (inputEmailEl) {
            inputEmailEl.classList.add('is-visible');
            inputEmailEl.focus();
          }
          btnSaveEmail.textContent = 'Confirmer mon e-mail';
          return;
        }

        const cellar = state.idealCellar || [];
        if (!cellar.length) {
          alert('Aucune sélection à sauvegarder.');
          return;
        }

        function formatVinForKlaviyo(p) {
          const hasDiscount = p.compareAtPrice && p.compareAtPrice > p.price;
          return {
            Titre: p.title,
            Prix: fmt(p.price),
            PrixBarre: hasDiscount ? fmt(p.compareAtPrice) : '',
            Quantite: p.packSize,
            Region: p.region ? p.region.toUpperCase() : '',
            Millesime: p.vintage || '',
            LienProduit: 'https://' + CONFIG.shopDomain + '/products/' + p.handle
          };
        }

        const vinsCourt = cellar.filter(function(p) { return p.horizon === 'court'; }).map(formatVinForKlaviyo);
        const vinsMoyen = cellar.filter(function(p) { return p.horizon === 'moyen'; }).map(formatVinForKlaviyo);
        const vinsLong  = cellar.filter(function(p) { return p.horizon === 'long';  }).map(formatVinForKlaviyo);

        const totalCost = cellar.reduce(function(sum, p) { return sum + p.price; }, 0);
        const totalBtl  = cellar.reduce(function(sum, p) { return sum + p.packSize; }, 0);

        const wantsOptin = document.getElementById('sim-save-optin').checked;
        const wantsPixel = document.getElementById('sim-save-pixel').checked;

        btnSaveEmail.disabled = true;
        btnSaveEmail.textContent = 'Envoi en cours…';
        const isContactRequest = simModalIntent === 'contact';

        fetch('https://a.klaviyo.com/client/events/?company_id=' + KLAVIYO_PUBLIC_KEY, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': '2024-10-15'
          },
          body: JSON.stringify({
            data: {
              type: 'event',
              attributes: {
                metric: {
                  data: {
                    type: 'metric',
                    attributes: { name: 'Cave_Ideale_Simulee' }
                  }
                },
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email: inputEmail }
                  }
                },
                properties: {
                  Budget_Total: fmt(totalCost),
                  Bouteilles_Total: totalBtl,
                  Recap_Items: getRecapItemsForKlaviyo(),
                  Vins_Court_Terme: vinsCourt,
                  Vins_Moyen_Terme: vinsMoyen,
                  Vins_Long_Terme: vinsLong
                }
              }
            }
          })
        })
        .then(function(response) {
          if (!(response.ok || response.status === 202)) {
            throw new Error('event HTTP ' + response.status);
          }

          const followUpCalls = [updateKlaviyoProfile(inputEmail, inputFirstName, inputLastName, wantsPixel)];
          if (wantsOptin) { followUpCalls.push(subscribeKlaviyoProfile(inputEmail, inputFirstName, inputLastName)); }

          // "Se faire contacter" : notifie l'équipe interne via MONITORING_EMAIL avec le détail
          // complet de la sélection (comme Cave_Ideale_Simulee) pour qu'un commercial n'ait pas
          // à redemander ce qui a déjà été calculé.
          if (isContactRequest) {
            followUpCalls.push(sendMonitoringEvent('Demande_Contact_Expert', {
              Email_Client: inputEmail,
              Prenom_Client: inputFirstName,
              Nom_Client: inputLastName,
              Telephone_Client: inputPhone || 'Non renseigné',
              Commentaire_Client: inputComment || 'Aucun',
              Budget_Total: fmt(totalCost),
              Bouteilles_Total: totalBtl,
              Vins_Court_Terme: vinsCourt,
              Vins_Moyen_Terme: vinsMoyen,
              Vins_Long_Terme: vinsLong,
            }));
          }

          return Promise.allSettled(followUpCalls);
        })
        .then(function() {
          if (isContactRequest) return null;
          btnSaveEmail.textContent = copy.submitPending;
          return generatePdfReport().catch(function(pdfErr) {
            ERR('PDF error:', pdfErr);
            alert("Votre simulation a bien été envoyée par e-mail, mais le PDF n'a pas pu être généré. Vous pouvez réessayer.");
          });
        })
        .then(function() {
          const msgEl = document.getElementById('sim-save-msg');
          if (msgEl) { msgEl.textContent = copy.successMsg; msgEl.style.display = 'block'; }
          btnSaveEmail.textContent = isContactRequest ? 'Demande envoyée !' : 'PDF téléchargé !';
        })
        .catch(function() {
          alert('Erreur de connexion.');
          btnSaveEmail.disabled = false;
          btnSaveEmail.textContent = copy.submitIdle;
        });
      });
    }

    const saveModal = document.getElementById('sim-save-modal');
    // Rattache la popin directement à <body> : si un ancêtre du thème Shopify porte un
    // CSS transform (bandeau sticky, animation au scroll...), il devient le repère de tout
    // élément descendant en position:fixed au lieu du vrai viewport — la popin s'ouvrirait
    // alors décalée et à une largeur "desktop", d'où l'effet de dézoom constaté sur mobile.
    if (saveModal && saveModal.parentElement !== document.body) {
      document.body.appendChild(saveModal);
    }
    let simModalScrollY = 0;
    let simModalIntent = 'pdf';
    const modalTitleEl = document.getElementById('sim-save-modal-title');
    const modalIntroEl = document.getElementById('sim-save-modal-intro');
    const modalSubmitEl = document.getElementById('sim-btn-save-email');
    const modalPhoneEl = document.getElementById('sim-save-phone');
    if (modalPhoneEl) {
      modalPhoneEl.addEventListener('input', function() {
        const cleaned = modalPhoneEl.value.replace(/[^0-9 +().-]/g, '');
        if (cleaned !== modalPhoneEl.value) modalPhoneEl.value = cleaned;
      });
    }
    const modalCommentWrapEl = document.querySelector('.sim-save-comment-wrap');
    const modalMsgEl = document.getElementById('sim-save-msg');
    const openSaveModalButtons = document.querySelectorAll('.sim-open-save-modal');
    for (let i = 0; i < openSaveModalButtons.length; i++) {
      openSaveModalButtons[i].addEventListener('click', function() {
        simModalIntent = this.dataset.intent === 'contact' ? 'contact' : 'pdf';
        const copy = MODAL_COPY[simModalIntent];
        if (modalTitleEl) modalTitleEl.textContent = copy.title;
        if (modalIntroEl) modalIntroEl.textContent = copy.intro;
        if (modalSubmitEl) { modalSubmitEl.textContent = copy.submitIdle; modalSubmitEl.disabled = false; }
        if (modalPhoneEl) modalPhoneEl.required = (simModalIntent === 'contact');
        if (modalCommentWrapEl) modalCommentWrapEl.hidden = (simModalIntent !== 'contact');
        // La popin est partagée entre les deux intentions (PDF / contact) : sans ce reset, le
        // message de succès d'une soumission précédente ("Votre PDF a été téléchargé !"…) restait
        // affiché si on rouvrait la popin ensuite pour l'autre intention.
        if (modalMsgEl) { modalMsgEl.style.display = 'none'; modalMsgEl.textContent = ''; }

        if (saveModal) saveModal.hidden = false;
        // Bloque le scroll de la page derrière la popin. overflow:hidden seul ne suffit pas
        // sur iOS Safari (rubber-band scroll persiste) : on fige aussi le body en
        // position:fixed avec un décalage top égal à la position de scroll actuelle, pour
        // ne pas sauter visuellement en haut de page à l'ouverture.
        simModalScrollY = window.scrollY || window.pageYOffset || 0;
        document.body.style.top = '-' + simModalScrollY + 'px';
        document.body.classList.add('sim-modal-open');
      });
    }
    const closeSaveModalButtons = document.querySelectorAll('[data-close-save-modal]');
    for (let i = 0; i < closeSaveModalButtons.length; i++) {
      closeSaveModalButtons[i].addEventListener('click', function() {
        if (saveModal) saveModal.hidden = true;
        // Le thème Shopify applique souvent scroll-behavior:smooth sur <html> — le temps que
        // body repasse en position:static (donc scrollY visuel = 0) puis que scrollTo
        // repositionne, ça produit un saut visible en haut de page avant de revenir. On
        // neutralise le smooth scroll le temps de cette restauration, qui doit être instantanée.
        const htmlEl = document.documentElement;
        const prevScrollBehavior = htmlEl.style.scrollBehavior;
        htmlEl.style.scrollBehavior = 'auto';
        document.body.classList.remove('sim-modal-open');
        document.body.style.top = '';
        window.scrollTo({ top: simModalScrollY, left: 0, behavior: 'instant' });
        htmlEl.style.scrollBehavior = prevScrollBehavior;
      });
    }


    // BOUTONS ET NAVIGATION QUIZ
    const options = document.querySelectorAll('.sim-option[data-answer]:not([data-field-override])');
    for (let i = 0; i < options.length; i++) {
      const btn = options[i];
      btn.addEventListener('click', function() {
        const step = btn.closest('.sim-step');
        const field = (step && step.dataset) ? step.dataset.field : null;
        if (!field) return;
        const brotherOptions = step.querySelectorAll('.sim-option:not([data-field-override])');
        for (let j = 0; j < brotherOptions.length; j++) {
          brotherOptions[j].classList.remove('is-selected');
        }
        btn.classList.add('is-selected');
        state.answers[field] = btn.dataset.answer;
        updateStepControls(step);
      });
    }

    const multiButtons = document.querySelectorAll('.sim-option-multi[data-answer]:not([data-field-override])');
    for (let i = 0; i < multiButtons.length; i++) {
      const btn = multiButtons[i];
      btn.addEventListener('click', function() {
        const step = btn.closest('.sim-step');
        const field = (step && step.dataset) ? step.dataset.field : null;
        if (!field) return;
        btn.classList.toggle('is-selected');
        const selected = step.querySelectorAll('.sim-option-multi.is-selected:not([data-field-override])');
        state.answers[field] = Array.prototype.slice.call(selected).map(function(b) { return b.dataset.answer; });
        updateStepControls(step);
      });
    }

    const nextActions = document.querySelectorAll('[data-action="next"]');
    for (let i = 0; i < nextActions.length; i++) {
      nextActions[i].addEventListener('click', next);
    }

    const skipActions = document.querySelectorAll('[data-action="skip"]');
    for (let i = 0; i < skipActions.length; i++) {
      skipActions[i].addEventListener('click', function() {
        const step = this.closest('.sim-step');
        const field = (step && step.dataset) ? step.dataset.field : null;
        if (field === 'budgetMax') {
          state.answers.budgetMax = null;
          if (maxBudgetInput) maxBudgetInput.value = '';
          if (noLimitBtn) noLimitBtn.classList.remove('is-selected');
        }
        if (field === 'regions') state.answers.regions = [];
        next();
      });
    }

    const prevActions = document.querySelectorAll('[data-action="prev"]');
    for (let i = 0; i < prevActions.length; i++) {
      prevActions[i].addEventListener('click', prev);
    }

    const finishActions = document.querySelectorAll('[data-action="finish"]');
    for (let i = 0; i < finishActions.length; i++) {
      finishActions[i].addEventListener('click', finish);
    }

    const restartBtn = document.getElementById('sim-restart-btn');
    if (restartBtn) {
      restartBtn.addEventListener('click', function() {
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        window.scrollTo(0, 0);
        location.reload();
      });
    }

    // Sélection panier : délégation sur document car les boutons/cases sont injectés
    // dynamiquement par renderCartInsert()/renderCards(), bien après ce premier appel à
    // bindEvents() — un addEventListener direct sur ces éléments ne trouverait rien à cet instant.
    document.addEventListener('click', function(event) {
      if (event.target.closest('.sim-cart-start')) {
        setCartSelectionMode(true);
      } else if (event.target.closest('.sim-cart-cancel')) {
        setCartSelectionMode(false);
      } else if (event.target.closest('.sim-cart-select-all')) {
        document.querySelectorAll('.sim-cart-check:not(:disabled)').forEach(function(cb) { cb.checked = true; });
        updateCartBarCount();
      } else if (event.target.closest('.sim-cart-select-none')) {
        document.querySelectorAll('.sim-cart-check:not(:disabled)').forEach(function(cb) { cb.checked = false; });
        updateCartBarCount();
      } else if (event.target.closest('.sim-cart-confirm')) {
        submitCartSelection();
      }
    });

    document.addEventListener('change', function(event) {
      if (event.target.classList && event.target.classList.contains('sim-cart-check')) {
        updateCartBarCount();
      }
    });

    // Redimensionnement : le texte de la barre fixed se réenroule différemment selon la largeur
    // (promo sur 1 ou 2 lignes...), donc sa hauteur — et par conséquent la marge de déclenchement
    // de sa disparition (syncCartEndObserver) — doivent être recalculées. Le padding réservé en
    // bas de page, lui, n'est PAS concerné : il est figé une fois pour toutes (voir
    // syncResultsBottomPadding()). Débounce léger pour ne pas forcer un reflow à chaque pixel
    // pendant un redimensionnement en continu (y compris les resize déclenchés sur mobile par
    // l'apparition/disparition de la barre d'adresse pendant le scroll).
    let resizePaddingTimer = null;
    window.addEventListener('resize', function() {
      clearTimeout(resizePaddingTimer);
      resizePaddingTimer = setTimeout(syncCartBarLayout, 150);
    });

    const debugToggle = document.querySelector('.sim-debug-toggle');
    const debugContent = document.getElementById('sim-debug');
    if (debugToggle && debugContent) {
      debugToggle.addEventListener('click', function() {
        const expanded = debugToggle.getAttribute('aria-expanded') === 'true';
        debugToggle.setAttribute('aria-expanded', String(!expanded));
        debugContent.hidden = expanded;
      });
    }

    const debugCopyBtn = document.querySelector('.sim-debug-copy');
    if (debugCopyBtn && debugContent) {
      debugCopyBtn.addEventListener('click', function() {
        const text = debugContent.textContent;
        const done = function() {
          const original = debugCopyBtn.textContent;
          debugCopyBtn.textContent = 'Copié !';
          setTimeout(function() { debugCopyBtn.textContent = original; }, 1500);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(done).catch(function() {
            fallbackCopy(text, done);
          });
        } else {
          fallbackCopy(text, done);
        }
      });
    }

    const feedbackToggle = document.querySelector('.sim-feedback-toggle');
    const feedbackForm = document.querySelector('.sim-feedback-form');
    if (feedbackToggle && feedbackForm) {
      feedbackToggle.addEventListener('click', function() {
        const expanded = feedbackToggle.getAttribute('aria-expanded') === 'true';
        feedbackToggle.setAttribute('aria-expanded', String(!expanded));
        feedbackForm.hidden = expanded;
      });
    }

    const feedbackSubmitBtn = document.getElementById('sim-feedback-submit');
    if (feedbackSubmitBtn) {
      feedbackSubmitBtn.addEventListener('click', function() {
        const textEl = document.getElementById('sim-feedback-text');
        const rawText = textEl ? textEl.value.trim() : '';
        const text = rawText.replace(/[<>]/g, '').slice(0, 600);
        if (!text) { if (textEl) textEl.focus(); return; }

        feedbackSubmitBtn.disabled = true;
        feedbackSubmitBtn.textContent = 'Envoi…';

        sendMonitoringEvent('Avis_Simulateur', { Avis: text })
          .then(function() {
            const msgEl = document.getElementById('sim-feedback-msg');
            if (msgEl) msgEl.hidden = false;
            if (textEl) textEl.value = '';
            feedbackSubmitBtn.textContent = 'Envoyé !';
          })
          .catch(function() {
            feedbackSubmitBtn.disabled = false;
            feedbackSubmitBtn.textContent = 'Envoyer';
            alert("L'envoi de votre avis a échoué. Réessayez.");
          });
      });
    }
  }

  function fallbackCopy(text, done) {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); done(); } catch (e) { ERR('Copie impossible:', e); }
    document.body.removeChild(ta);
  }

  // Messages qui défilent pendant le chargement — donnent une impression de progression sans
  // être réellement liés à l'avancement du pipeline (poudre aux yeux assumée). L'intervalle
  // n'est jamais forcé : si le chargement est rapide, on s'arrête simplement après 1 ou 2
  // messages plutôt que de tous les afficher d'un coup ou de rallonger artificiellement l'attente.
  const LOADING_MESSAGES = [
    'Composition sur-mesure de votre cave idéale en cours…',
    'Recherche des vins disponibles dans notre catalogue…',
    'Analyse de vos critères (couleurs, régions, budget)…',
    'Sélection des meilleures références…',
    'Équilibrage de votre cave par horizon de garde…',
  ];
  const LOADING_MESSAGE_INTERVAL_MS = 2200;
  let loadingMessageTimer = null;

  function showLoader(v) {
    const el = document.getElementById('simulateur-loading');
    if (el) el.classList.toggle('is-visible', v);

    if (loadingMessageTimer) {
      clearInterval(loadingMessageTimer);
      loadingMessageTimer = null;
    }

    if (v) {
      const textEl = document.getElementById('sim-loading-text');
      if (textEl) {
        let idx = 0;
        textEl.textContent = LOADING_MESSAGES[idx];
        loadingMessageTimer = setInterval(function() {
          idx = (idx + 1) % LOADING_MESSAGES.length;
          textEl.textContent = LOADING_MESSAGES[idx];
        }, LOADING_MESSAGE_INTERVAL_MS);
      }
    }
  }

  // Réservée aux vraies pannes techniques (réseau, timeout, rate-limit, erreur Shopify) — le
  // cas "aucun vin ne correspond" passe désormais par l'écran .sim-no-result de renderResults().
  function showError(msg) {
    const el = document.getElementById('simulateur-error');
    if (el) {
      el.innerHTML = '<p class="sim-error-message">Erreur : ' + escapeHtml(msg) + '</p>' +
        renderContactCta('sim-error-contact', 'Nos experts peuvent construire votre cave sur mesure et dénicher les bouteilles qu\'il vous faut. Échangeons ensemble.') +
        '<div class="sim-error-retry-wrap">' +
        '<button class="sim-btn sim-btn-ghost sim-error-retry" type="button">Réessayer</button>' +
        '</div>';
      el.classList.add('is-visible');
      const retryBtn = el.querySelector('.sim-error-retry');
      if (retryBtn) {
        retryBtn.addEventListener('click', function() {
          el.classList.remove('is-visible');
          run();
        });
      }
    }
    showLoader(false);
  }

  // ---- INITIALISATION SÉCURISÉE ----
  function initSimulateur() {
    if (!document.getElementById('simulateur-quiz')) return;
    showStep(0);
    bindEvents();
  }

  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    initSimulateur();
  } else {
    document.addEventListener('DOMContentLoaded', initSimulateur);
  }

})();